// ============================================================================
// TIME ALLOCATION VIEW - Vertical schedule with time slots and user columns
// Extracted from WorkItemsKanbanBoard for maintainability
// Date: February 2026
// ============================================================================

'use client';

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  rectSortingStrategy,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDroppable } from '@dnd-kit/core';
import {
  Clock,
  CalendarOff,
  CalendarClock,
  Palmtree,
  Calendar,
  User,
  MapPin,
  GripVertical,
  Send,
  Users,
  UserCheck,
  ChevronUp,
  ChevronDown,
  RotateCcw,
  Lock,
  Flag,
  Navigation,
  Timer,
  MoreVertical,
  ListFilter,
  Briefcase,
  ChevronRight,
  ClipboardList,
  Package,
  Truck,
} from 'lucide-react';
import AppointmentCard, { type AppointmentCardAssignee } from '@/components/work-items/AppointmentCard';
import WorkItemHoverCard from '@/components/work-items/WorkItemHoverCard';
import { format, setHours, setMinutes, startOfDay, differenceInMinutes, isSameDay } from 'date-fns';
import {
  formatTimeSAST,
  formatLocalISOSAST,
  formatTimeRangeSAST,
  getHourFractionSAST,
  SAST_TIMEZONE,
} from '@/lib/sastTime';
import {
  buildAppointmentGroupLeaderNameMap,
  getAppointmentDisplayNumber,
  getAppointmentGroupLeaderDisplayName,
  isAppointmentGroupLeader,
  isAppointmentLikeItem,
} from '@/lib/appointmentDisplay';
import { buildSmartTaskAppointmentCard, buildSmartTaskPanelBanner } from '@/app/client-zone/modules/total-operations/components/smart-task/utils/smartTaskAppointmentCard';
import { getWorkItemOwnershipSummary } from '@/app/client-zone/modules/total-operations/components/smart-task/utils/workItemOwnership';
import { getToneForAppointmentStatusKey, formatAppointmentDisplayStatusLabel } from '@/lib/appointmentStatusVisual';
import { buildWorkItemTooltipLines } from '@/lib/workItemTooltip';
import type { WorkItem } from '../UniversalWorkItems';
import type { WorkItemTemplate } from '../WorkItemModal';
import { useTheme } from '@/contexts/ThemeContext';
import { authService } from '@/lib/authService';
import { canSeeAppointmentRescheduleState } from '@/lib/work-items/appointmentRescheduleState';
import { confirm, notify } from '@/lib/notifications';
import LocationPickerModal from '../LocationPickerModal';
import { type CustomLocationData } from '../maps/LocationAutocomplete';
import { getColumnHeaderColors, type KanbanStylePreset } from './KanbanStylePresets';
import ScheduleSidebarItemCard from './ScheduleSidebarItemCard';
import ScheduleSidebarShell, { SCHEDULE_SIDEBAR_WIDTH_CLASS } from './ScheduleSidebarShell';
import { buildOverlapLaneLayout } from './scheduleOverlapLayout';
import {
  getScheduleSidebarContactOptions,
  getUnscheduledJobsForSelectedDate,
  type ScheduleSidebarSortOrder,
} from './scheduleFilters';
import { ZOOM_SETTINGS, COLOR_CLASSES, TIME_ALLOC_PRIORITY_COLORS, getKBAvatarColor, getKBInitials, getKBInitialsFromName } from './kanban-shared';
import type { WorkflowState, AssignedUser, ZoomLevel, UserLeave } from './kanban-shared';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';
import {
  getAssignmentUserIdFromValue,
  normalizeAssignedUserId,
  parseAssignmentSourceArray,
} from '@/lib/work-items/scheduleAssignments';

// Re-export types needed by consumers
export type { WorkflowState, AssignedUser, ZoomLevel, UserLeave };
export { ZOOM_SETTINGS, COLOR_CLASSES, TIME_ALLOC_PRIORITY_COLORS };

const KANBAN_ITEM_TYPE_ICONS: Record<string, React.ElementType> = {
  job: Briefcase,
  task: ClipboardList,
  appointment: Calendar,
  delivery: Truck,
  route: Truck,
  project: Package,
};

const TIMELINE_CARD_HORIZONTAL_INSET_PX = 6;
const TIMELINE_CARD_LANE_GAP_PX = 4;
const TIME_ALLOCATION_CARD_DRAG_ID_PREFIX = 'ta-card::';
const TIME_ALLOCATION_CURRENT_TIME_LINE_CLASS = 'bg-blue-500 dark:bg-cyan-300';

function buildTimeAllocationCardDragId(containerId: string, itemId: string) {
  return `${TIME_ALLOCATION_CARD_DRAG_ID_PREFIX}${containerId}::${itemId}`;
}

function resolveTimeAllocationColumnAppearance(presetConfig: KanbanStylePreset | undefined, baseColor?: string) {
  const colorKey = typeof baseColor === 'string' && baseColor.trim().length > 0 ? baseColor : 'gray';
  const colorClass = COLOR_CLASSES[colorKey] || COLOR_CLASSES.gray;
  const columnStyles = presetConfig?.column;
  const headerColors = presetConfig ? getColumnHeaderColors(presetConfig.id, colorKey) : null;
  const usesGeneratedGradient = !columnStyles?.headerBackground
    || (columnStyles.headerBackground.includes('gradient') && !columnStyles.headerBackground.includes('from-'));
  const effectiveHeaderBackground = headerColors && usesGeneratedGradient
    ? `bg-gradient-to-r ${headerColors.background}`
    : (columnStyles?.headerBackground || '');
  const effectiveHeaderText = headerColors?.text || columnStyles?.headerTextColor || colorClass.headingText;
  const effectiveSubtitleText = columnStyles?.subtitleTextColor || effectiveHeaderText;
  const effectiveColumnBorder = headerColors && columnStyles
    ? `${columnStyles.border} ${headerColors.border}`
    : (columnStyles?.border || '');
  const columnClasses = columnStyles
    ? `${columnStyles.background} ${columnStyles.borderRadius} ${effectiveColumnBorder} ${columnStyles.shadow}`
    : `${colorClass.header} rounded-xl border-2`;

  return {
    colorClass,
    columnStyles,
    effectiveHeaderBackground,
    effectiveHeaderText,
    effectiveSubtitleText,
    effectiveColumnBorder,
    columnClasses,
  };
}

function getTimeAllocationHeaderRadiusClass(columnStyles?: KanbanStylePreset['column']) {
  return columnStyles?.borderRadius
    ? `${columnStyles.borderRadius} rounded-b-none`
    : '';
}

function getTimeAllocationLanePlacement(laneIndex: number, laneCount: number): { left: string; width: string } {
  if (laneCount <= 1) {
    return {
      left: `${TIMELINE_CARD_HORIZONTAL_INSET_PX}px`,
      width: `calc(100% - ${TIMELINE_CARD_HORIZONTAL_INSET_PX * 2}px)`,
    };
  }

  const totalGapPx = (laneCount - 1) * TIMELINE_CARD_LANE_GAP_PX;
  const laneWidthExpression = `((100% - ${TIMELINE_CARD_HORIZONTAL_INSET_PX * 2 + totalGapPx}px) / ${laneCount})`;

  return {
    left: `calc(${TIMELINE_CARD_HORIZONTAL_INSET_PX}px + ${laneIndex} * (${laneWidthExpression} + ${TIMELINE_CARD_LANE_GAP_PX}px))`,
    width: `calc(${laneWidthExpression})`,
  };
}

type AssignmentAwareWorkItem = WorkItem & {
  assigned_users?: unknown;
  assigned_users_details?: unknown;
  assigned_to_name?: string | null;
};

function normalizeAssignmentName(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function asAssignmentRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function getAssignmentUserNameFromRecord(record: Record<string, unknown>): string | null {
  const joinedName = [record.first_name, record.last_name]
    .map(normalizeAssignmentName)
    .filter((value): value is string => Boolean(value))
    .join(' ')
    .trim();

  if (joinedName) {
    return joinedName;
  }

  const candidateKeys = ['name', 'full_name', 'display_name', 'label', 'user_name', 'email'];
  for (const key of candidateKeys) {
    const candidate = normalizeAssignmentName(record[key]);
    if (candidate) {
      return candidate;
    }
  }

  return null;
}

function getAssignmentCustomFields(item: WorkItem): Record<string, unknown> {
  return asAssignmentRecord(item.custom_fields) ?? {};
}

function getAppointmentAssignees(
  item: WorkItem,
  assignedUsers: AssignedUser[] = [],
  fallbackName?: string | null,
): AppointmentCardAssignee[] {
  const assignmentAwareItem = item as AssignmentAwareWorkItem;
  const customFields = getAssignmentCustomFields(item);
  const assigneeMap = new Map<string, AppointmentCardAssignee>();
  const primaryAssignedUserId = normalizeAssignedUserId(item.assigned_to);

  const registerAssignee = (userId: number | null, name: string | null, isPrimary = false) => {
    const resolvedUserId = userId ?? null;
    const resolvedName = name?.trim() || (resolvedUserId != null
      ? assignedUsers.find((user) => user.id === resolvedUserId)
        ? [assignedUsers.find((user) => user.id === resolvedUserId)?.first_name, assignedUsers.find((user) => user.id === resolvedUserId)?.last_name]
            .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
            .join(' ')
            .trim() || assignedUsers.find((user) => user.id === resolvedUserId)?.email || null
        : null
      : null);

    if (!resolvedName) {
      return;
    }

    const key = resolvedUserId != null ? `id:${resolvedUserId}` : `name:${resolvedName.toLowerCase()}`;
    const existing = assigneeMap.get(key);

    if (existing) {
      assigneeMap.set(key, {
        ...existing,
        id: existing.id ?? resolvedUserId,
        name: existing.name || resolvedName,
        isPrimary: existing.isPrimary || isPrimary,
      });
      return;
    }

    assigneeMap.set(key, {
      id: resolvedUserId,
      name: resolvedName,
      isPrimary,
    });
  };

  if (Array.isArray(item.assignees)) {
    item.assignees.forEach((assignee) => {
      const assigneeId = normalizeAssignedUserId(assignee.user_id);
      const assigneeName = [assignee.first_name, assignee.last_name]
        .map(normalizeAssignmentName)
        .filter((value): value is string => Boolean(value))
        .join(' ')
        .trim() || normalizeAssignmentName(assignee.email);
      registerAssignee(assigneeId, assigneeName, assigneeId != null && assigneeId === primaryAssignedUserId);
    });
  }

  [
    assignmentAwareItem.assigned_users_details,
    assignmentAwareItem.assigned_users,
    customFields.assigned_users_details,
    customFields.assigned_users,
    customFields.assigned_user_ids,
    customFields.participant_user_ids,
  ].forEach((source) => {
    parseAssignmentSourceArray(source).forEach((entry) => {
      const directUserId = normalizeAssignedUserId(entry);
      if (directUserId != null) {
        registerAssignee(directUserId, null, directUserId === primaryAssignedUserId);
        return;
      }

      const record = asAssignmentRecord(entry);
      if (!record) {
        return;
      }

      const recordUserId = getAssignmentUserIdFromValue(record);
      const recordUserName = getAssignmentUserNameFromRecord(record);
      registerAssignee(recordUserId, recordUserName, recordUserId != null && recordUserId === primaryAssignedUserId);
    });
  });

  if (primaryAssignedUserId != null) {
    registerAssignee(primaryAssignedUserId, normalizeAssignmentName(assignmentAwareItem.assigned_to_name), true);
  }

  if (assigneeMap.size === 0 && fallbackName?.trim()) {
    registerAssignee(primaryAssignedUserId, fallbackName.trim(), true);
  }

  return Array.from(assigneeMap.values()).sort((left, right) => {
    if (left.isPrimary === right.isPrimary) {
      return left.name.localeCompare(right.name);
    }

    return left.isPrimary ? -1 : 1;
  });
}

function getAppointmentAssigneeName(
  item: WorkItem,
  assignedUsers: AssignedUser[] = [],
  fallbackName?: string | null,
): string | null {
  if (item.assigned_team && fallbackName) {
    return fallbackName;
  }

  return getAppointmentAssignees(item, assignedUsers, fallbackName)[0]?.name || fallbackName || null;
}

function buildScheduleNotificationAppointments(appointments: WorkItem[]) {
  return appointments
    .filter((appointment) => appointment.item_type === 'appointment')
    .map((appointment) => ({
      id: appointment.id,
      title: appointment.title,
      item_number: appointment.item_number,
      scheduled_start: appointment.scheduled_start,
      scheduled_end: appointment.scheduled_end,
      location_address: appointment.location_address,
      description: appointment.description,
      parent_job_number: (appointment as WorkItem & { parent_job_number?: string | null }).parent_job_number || null,
    }));
}

async function sendScheduleNotificationToUser(options: {
  userId: number;
  scheduleDate: Date;
  appointments: WorkItem[];
}) {
  const token = authService.getTokens()?.access_token;
  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch('/api/v1/schedule-threads/send-schedule', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    credentials: 'include',
    body: JSON.stringify({
      user_id: options.userId,
      schedule_date: options.scheduleDate.toISOString(),
      appointments: buildScheduleNotificationAppointments(options.appointments),
    }),
  });

  const data = await response.json().catch(() => null);

  if (!response.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to send schedule');
  }

  return data;
}

// ============================================================================
// SORTABLE TIME ALLOCATION COLUMN WRAPPER
// Wraps TimeAllocationView column for drag-to-reorder functionality
// ============================================================================

interface SortableTimeAllocationColumnProps {
  column: WorkflowState;
  itemsByColumn: Record<string, WorkItem[]>;
  selectedDate: Date;
  groupLeaderNamesByGroupKey?: Map<string, string>;
  getTemplate: (item: WorkItem) => WorkItemTemplate | null;
  onViewItem: (itemId: string) => void;
  onRefresh?: () => Promise<void>;
  getItemStyle: (item: WorkItem) => { top: number; height: number; isScheduled: boolean };
  hourSlots: { hour: number; displayHour: number; label: string; dayOffset: number }[];
  zoomLevel: ZoomLevel;
  zoomSettings: typeof ZOOM_SETTINGS[ZoomLevel];
  START_HOUR: number;
  HOUR_HEIGHT: number;
  hoveredHour: { columnKey: string; hour: number } | null;
  checkForClashes?: (userId: number, newStart: Date, newEnd: Date, excludeItemId?: string) => WorkItem[];
  /** Add people to the same appointment so it appears on their schedules */
  onOpenAppointmentAssignees?: (item: WorkItem) => void;
  isTimeSlotInPast: (hour: number, dayOffset?: number) => boolean;
  userLeave?: UserLeave | null;
  presetConfig?: KanbanStylePreset;
  onRollHours: (direction: 'up' | 'down') => void;
  visibleStartHour: number;
  visibleHoursCount: number;
  defaultVisibleStartHour: number;
  onSetVisibleStartHour: (hour: number) => void;
  onOpenTeamMember?: (userId: number) => void;
  assignedUsers?: AssignedUser[];
  hideTimeWindowControls?: boolean;
  workItemsById: Map<string, WorkItem>;
  currentTimeOffsetPx: number | null;
}

function SortableTimeAllocationColumn({
  column,
  itemsByColumn,
  selectedDate,
  groupLeaderNamesByGroupKey,
  getTemplate,
  onViewItem,
  onRefresh,
  getItemStyle,
  hourSlots,
  zoomLevel,
  zoomSettings,
  START_HOUR,
  HOUR_HEIGHT,
  hoveredHour,
  checkForClashes,
  onOpenAppointmentAssignees,
  isTimeSlotInPast,
  userLeave,
  presetConfig,
  onRollHours,
  visibleStartHour,
  visibleHoursCount,
  defaultVisibleStartHour,
  onSetVisibleStartHour,
  onOpenTeamMember,
  assignedUsers = [],
  hideTimeWindowControls = false,
  workItemsById,
  currentTimeOffsetPx,
}: SortableTimeAllocationColumnProps) {
  const sortableColumnId = useMemo(() => `column-${column.key}`, [column.key]);

  const {
    attributes,
    listeners,
    setNodeRef: setSortableRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: sortableColumnId,
  });

  const isOver = false;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const columnItems = useMemo(() => itemsByColumn[column.key] || [], [itemsByColumn, column.key]);
  const {
    colorClass,
    columnStyles,
    effectiveHeaderBackground,
    effectiveHeaderText,
    effectiveSubtitleText,
    effectiveColumnBorder,
    columnClasses,
  } = resolveTimeAllocationColumnAppearance(presetConfig, column.color);
  
  // Find the assigned user for this column's letter avatar
  const isScheduleUserColumn = column.key.startsWith('user-');
  const isScheduleTeamColumn = column.key.startsWith('team-');
  const scheduleUserId = isScheduleUserColumn ? parseInt(column.key.replace('user-', '')) : null;
  const scheduleUser = scheduleUserId ? assignedUsers.find(u => u.id === scheduleUserId) : null;
  const teamInitials = isScheduleTeamColumn ? getKBInitialsFromName(column.label) : null;
  const teamMemberCount = column.memberUserIds?.length ?? 0;
  
  // Filter out jobs that have appointments IN THIS COLUMN
  // Jobs can still appear in other users' columns as unscheduled even if they have appointments elsewhere
  const columnJobsWithAppointments = useMemo(() => {
    const jobIds = new Set<string>();
    columnItems.forEach(item => {
      if (item.item_type === 'appointment' && item.parent_id) {
        jobIds.add(item.parent_id);
      }
    });
    return jobIds;
  }, [columnItems]);
  
  const filteredItems = useMemo(() => {
    return columnItems.filter(item => {
      if (item.item_type === 'job' && columnJobsWithAppointments.has(item.id)) {
        return false;
      }
      return true;
    });
  }, [columnItems, columnJobsWithAppointments]);
  
  // Split items into scheduled (for today) — unscheduled items now shown in unified sidebar
  const scheduledItems = useMemo(() => {
    return filteredItems.filter((item) => getItemStyle(item).isScheduled);
  }, [filteredItems, getItemStyle]);

  const scheduledLayouts = useMemo(() => {
    const scheduledEntries = scheduledItems.map((item) => ({
      item,
      itemStyle: getItemStyle(item),
    }));

    return buildOverlapLaneLayout(scheduledEntries, {
      getId: (entry) => entry.item.id,
      getStart: (entry) => entry.itemStyle.top,
      getEnd: (entry) => entry.itemStyle.top + Math.max(entry.itemStyle.height, 30),
    });
  }, [scheduledItems, getItemStyle]);
  const scheduledItemDragIds = useMemo(
    () => scheduledItems.map((item) => buildTimeAllocationCardDragId(`${column.key}:timeline`, item.id)),
    [column.key, scheduledItems]
  );

  const canRollUp = visibleStartHour > -12;
  const canRollDown = visibleStartHour + visibleHoursCount < 36;

  return (
    <div ref={setSortableRef} style={style} {...attributes}>
      <div
        className={`
          flex flex-col overflow-hidden ${zoomSettings.columnWidth} flex-shrink-0 
          ${columnClasses}
          ${isOver ? 'ring-2 ring-blue-500 scale-[1.01] shadow-xl' : (columnStyles ? '' : colorClass.border)}
          transition-all duration-150 ease-out
        `}
        style={{ height: 'auto' }}
      >
        {/* Column Header with drag handle */}
        <div className="sticky top-0 z-30">
          <div className={`${zoomSettings.headerPadding} border-b border-gray-200/50 dark:border-gray-600/50 ${effectiveHeaderBackground} ${getTimeAllocationHeaderRadiusClass(columnStyles)}`}>
            <div className={`flex items-center ${zoomLevel === 'compact' ? 'flex-col gap-1' : 'justify-between mb-2'}`}>
              <div className="flex items-center gap-2">
                {/* Drag handle */}
                <div 
                  {...listeners}
                  className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                  title="Drag to reorder column"
                >
                  <GripVertical className="w-4 h-4 text-gray-400" />
                </div>
                {scheduleUser ? (
                  <button
                    type="button"
                    className="cursor-pointer hover:ring-2 hover:ring-orange-400 rounded-full transition-all"
                    onClick={(e) => { e.stopPropagation(); if (onOpenTeamMember && scheduleUserId) onOpenTeamMember(scheduleUserId); }}
                    title={`View ${scheduleUser.first_name} ${scheduleUser.last_name} profile`}
                  >
                    <div className={`${zoomLevel === 'compact' ? 'w-7 h-7' : 'w-9 h-9'} rounded-full flex items-center justify-center text-white ${zoomLevel === 'compact' ? 'text-[9px]' : 'text-xs'} font-semibold shadow-sm ${getKBAvatarColor(scheduleUser.email, scheduleUser.first_name)}`}>
                      {getKBInitials(scheduleUser.first_name, scheduleUser.last_name, scheduleUser.email)}
                    </div>
                  </button>
                ) : isScheduleTeamColumn ? (
                  <div
                    className={`${zoomLevel === 'compact' ? 'w-7 h-7 text-[9px]' : 'w-9 h-9 text-xs'} rounded-full flex items-center justify-center text-white font-semibold shadow-sm bg-gradient-to-br from-teal-500 to-blue-600`}
                    title={`${column.label}${teamMemberCount > 0 ? ` · ${teamMemberCount} members` : ''}`}
                  >
                    {teamInitials || 'TM'}
                  </div>
                ) : (
                  <div className={`p-${zoomLevel === 'compact' ? '1.5' : '2'} rounded-lg ${colorClass.iconBg} text-white shadow-sm`}>
                    <User className={`${zoomLevel === 'compact' ? 'w-3 h-3' : 'w-4 h-4'}`} />
                  </div>
                )}
                <div>
                  {scheduleUserId ? (
                    <button
                      type="button"
                      className={`font-semibold hover:underline cursor-pointer ${effectiveHeaderText} ${zoomSettings.headerText}`}
                      onClick={(e) => { e.stopPropagation(); if (onOpenTeamMember && scheduleUserId) onOpenTeamMember(scheduleUserId); }}
                      title={column.label}
                    >
                      {column.label}
                    </button>
                  ) : (
                    <div className={`font-semibold ${effectiveHeaderText} ${zoomSettings.headerText}`}>
                      {column.label}
                    </div>
                  )}
                  {column.description && zoomLevel !== 'compact' && (
                    <p className={`text-xs ${effectiveSubtitleText}`}>{column.description}</p>
                  )}
                </div>
              </div>
              <span className={`font-bold ${columnStyles?.countBadge ? `${columnStyles.countBadge.background} ${columnStyles.countBadge.textColor} ${columnStyles.countBadge.borderRadius}` : `${colorClass.headingText} bg-white/50 dark:bg-black/20 rounded-full`} ${zoomSettings.badgeSize} px-2 py-1`}>
                {columnItems.length}
              </span>
            </div>
            
            {/* Send Schedule Now Button */}
            {isScheduleUserColumn && column.userId && (
              <button
                onClick={async (event: React.MouseEvent<HTMLButtonElement>) => {
                  const button = event.currentTarget;
                  const scheduleNotificationUserId = column.userId;
                  if (scheduleNotificationUserId == null) {
                    return;
                  }
                  try {
                    await sendScheduleNotificationToUser({
                      userId: scheduleNotificationUserId,
                      scheduleDate: selectedDate,
                      appointments: scheduledItems,
                    });
                    notify.success('Schedule sent successfully');

                    const originalContent = button.innerHTML;
                    button.innerHTML = '✓ Sent!';
                    button.disabled = true;
                    window.setTimeout(() => {
                      button.innerHTML = originalContent;
                      button.disabled = false;
                    }, 2000);
                  } catch (error: unknown) {
                    console.error('[Kanban] ❌ Error sending schedule:', error);
                    notify.error(`Failed to send schedule: ${error instanceof Error ? error.message : 'Unknown error'}`);
                  }
                }}
                className="w-full mt-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-1.5"
              >
                <Send className="w-3 h-3" />
                Send Schedule Now
              </button>
            )}
          </div>
          
          <div className={`flex items-center justify-end border-b border-gray-200/50 px-2 py-1 dark:border-gray-700/50 ${columnStyles?.background || 'bg-white/95 dark:bg-gray-900/95'} backdrop-blur-sm`}>
          <button
            type="button"
            onClick={() => canRollUp && onRollHours('up')}
            disabled={!canRollUp}
            className={`inline-flex h-6 w-6 items-center justify-center rounded-full transition-colors ${
              canRollUp
                ? 'text-orange-500 hover:bg-orange-50 dark:text-orange-400 dark:hover:bg-orange-900/30'
                : 'cursor-not-allowed text-gray-300 dark:text-gray-600'
            }`}
            title={canRollUp ? 'Show earlier hours' : 'Already at earliest hours'}
            aria-label="Show earlier hours"
          >
            <ChevronUp className="h-3.5 w-3.5" />
          </button>
          </div>
        </div>
        
        {/* Time slots with scheduled items */}
        <div 
          data-column-id={column.key}
          data-timeline-area={column.key}
          className={`relative`} 
          style={{ height: hourSlots.length * HOUR_HEIGHT }}
        >
          {/* Hour grid lines with alternating shading and inline hour labels */}
          {hourSlots.map((slot, index) => {
            const isEven = index % 2 === 0;
            const isPast = isTimeSlotInPast(slot.displayHour, slot.dayOffset);
            // Check if this slot is during leave
            const isOnLeave = !!userLeave;
            const isHalfDayMorning = userLeave?.is_half_day && userLeave?.half_day_period === 'morning';
            const isHalfDayAfternoon = userLeave?.is_half_day && userLeave?.half_day_period === 'afternoon';
            // Morning: 06:00-12:00, Afternoon: 12:00-18:00
            const isLeaveSlot = isOnLeave && (
              !userLeave.is_half_day || // Full day leave covers all slots
              (isHalfDayMorning && slot.displayHour < 12) || // Morning half day
              (isHalfDayAfternoon && slot.displayHour >= 12) // Afternoon half day
            );
            
            // Create alternating shades based on column color
            let bgClass = '';
            if (isLeaveSlot && !isPast) {
              // Leave time slots - teal/cyan color to differentiate from past (gray)
              bgClass = 'bg-teal-200/80 dark:bg-teal-800/60';
            } else if (isPast) {
              // Greyed out for past time slots
              bgClass = 'bg-gray-200/70 dark:bg-gray-700/50';
            } else if (column.color === 'blue') {
              bgClass = isEven ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-blue-100 dark:bg-blue-900/30';
            } else if (column.color === 'purple') {
              bgClass = isEven ? 'bg-purple-50 dark:bg-purple-900/20' : 'bg-purple-100 dark:bg-purple-900/30';
            } else if (column.color === 'green') {
              bgClass = isEven ? 'bg-green-50 dark:bg-green-900/20' : 'bg-green-100 dark:bg-green-900/30';
            } else if (column.color === 'orange') {
              bgClass = isEven ? 'bg-orange-50 dark:bg-orange-900/20' : 'bg-orange-100 dark:bg-orange-900/30';
            } else if (column.color === 'red') {
              bgClass = isEven ? 'bg-red-50 dark:bg-red-900/20' : 'bg-red-100 dark:bg-red-900/30';
            } else if (column.color === 'yellow') {
              bgClass = isEven ? 'bg-yellow-50 dark:bg-yellow-900/20' : 'bg-yellow-100 dark:bg-yellow-900/30';
            } else {
              bgClass = isEven ? 'bg-gray-50 dark:bg-gray-800/50' : 'bg-gray-100 dark:bg-gray-800/70';
            }
            
            // Check if this hour is being hovered during drag
            const isHovered = hoveredHour?.columnKey === column.key && hoveredHour?.hour === slot.hour;
            
            return (
              <div
                key={slot.hour}
                className={`absolute w-full border-b border-gray-200/30 dark:border-gray-700/30 transition-all duration-100 ${bgClass} ${
                  isHovered && !isPast && !isLeaveSlot ? 'ring-4 ring-blue-400 ring-inset bg-blue-100/60 dark:bg-blue-900/60 z-10' : ''
                } ${isPast || isLeaveSlot ? 'cursor-not-allowed' : ''}`}
                style={{ top: (slot.hour - START_HOUR) * HOUR_HEIGHT, height: HOUR_HEIGHT }}
              >
                {/* Inline hour label */}
                <span className={`absolute top-0.5 left-1 text-[10px] font-semibold select-none pointer-events-none z-[1] ${
                  isPast ? 'text-gray-400/70 dark:text-gray-600' : 'text-gray-400 dark:text-gray-500'
                }`}>
                  {slot.label}
                </span>
              </div>
            );
          })}
          
          {/* Leave indicator overlay - shown when user is on leave */}
          {userLeave && (
            <div 
              className="absolute inset-0 pointer-events-none flex items-start justify-center z-20"
              style={{ 
                top: userLeave.is_half_day && userLeave.half_day_period === 'afternoon' 
                  ? (12 - START_HOUR) * HOUR_HEIGHT 
                  : 0,
                height: userLeave.is_half_day 
                  ? 6 * HOUR_HEIGHT // Half day = 6 hours
                  : hourSlots.length * HOUR_HEIGHT
              }}
            >
              <div 
                className="mt-4 bg-teal-600/90 dark:bg-teal-700/90 text-white px-3 py-2 rounded-lg shadow-lg flex items-center gap-2 text-sm font-medium"
                style={{ 
                  backgroundColor: userLeave.leave_type_color ? `${userLeave.leave_type_color}E6` : undefined 
                }}
              >
                <Palmtree className="w-4 h-4" />
                <span>{userLeave.leave_type_name}</span>
                {userLeave.is_half_day && (
                  <span className="text-xs opacity-80">
                    ({userLeave.half_day_period === 'morning' ? 'AM' : 'PM'})
                  </span>
                )}
              </div>
            </div>
          )}
          
          {/* Scheduled items - use rectSortingStrategy to prevent items from moving during drag */}
          <SortableContext items={scheduledItemDragIds} strategy={rectSortingStrategy}>
            {scheduledLayouts.map((layout) => {
              const { item, itemStyle } = layout.item;
              const { top, height } = itemStyle;
              const template = getTemplate(item);
              const priorityBorderColor = {
                urgent: 'border-l-red-600',
                critical: 'border-l-red-700',
                high: 'border-l-orange-500',
                medium: 'border-l-yellow-500',
                low: 'border-l-green-500',
              }[item.priority || 'medium'] || 'border-l-gray-400';
              
              return (
                <TimeAllocationCard
                  key={item.id}
                  item={item}
                  dragContainerId={`${column.key}:timeline`}
                  template={template}
                  priorityBorderColor={priorityBorderColor}
                  groupLeaderNamesByGroupKey={groupLeaderNamesByGroupKey}
                  onViewItem={onViewItem}
                  onRefresh={onRefresh}
                  isTimeline={true}
                  top={top}
                  height={Math.max(height, 30)}
                      checkForClashes={checkForClashes}
                      onOpenAppointmentAssignees={onOpenAppointmentAssignees}
                      presetConfig={presetConfig}
                      assignedUsers={assignedUsers}
                      assigneeName={column.label}
                      laneIndex={layout.laneIndex}
                      laneCount={layout.laneCount}
                      itemsById={workItemsById}
                    />
                  );
                })}
              </SortableContext>
            </div>
        <div className="flex items-center justify-between border-t border-gray-200/50 px-2 py-1 dark:border-gray-700/50">
          <button
            type="button"
            onClick={() => canRollDown && onRollHours('down')}
            disabled={!canRollDown}
            className={`inline-flex h-6 w-6 items-center justify-center rounded-full transition-colors ${
              canRollDown
                ? 'text-orange-500 hover:bg-orange-50 dark:text-orange-400 dark:hover:bg-orange-900/30'
                : 'cursor-not-allowed text-gray-300 dark:text-gray-600'
            }`}
            title={canRollDown ? 'Show later hours' : 'Already at latest hours'}
            aria-label="Show later hours"
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </button>

          {visibleStartHour !== defaultVisibleStartHour && (
            <button
              type="button"
              onClick={() => onSetVisibleStartHour(defaultVisibleStartHour)}
              className="inline-flex h-6 w-6 items-center justify-center rounded-full text-blue-500 transition-colors hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/30"
              title={`Reset to default hours (${defaultVisibleStartHour.toString().padStart(2, '0')}:00 start)`}
              aria-label="Reset visible hours"
            >
              <RotateCcw className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// TIME ALLOCATION CARD - Draggable card for Time Allocation view
// ============================================================================
interface TimeAllocationCardProps {
  item: WorkItem;
  dragContainerId?: string;
  template: WorkItemTemplate | null;
  priorityBorderColor: string;
  groupLeaderNamesByGroupKey?: Map<string, string>;
  onViewItem: (itemId: string) => void;
  onRefresh?: () => Promise<void>;
  isTimeline: boolean;
  top?: number;
  height?: number;
  checkForClashes?: (userId: number, newStart: Date, newEnd: Date, excludeItemId?: string) => WorkItem[];
  /** Add people to the same appointment so it appears on their schedules */
  onOpenAppointmentAssignees?: (item: WorkItem) => void;
  presetConfig?: KanbanStylePreset;
  assignedUsers?: AssignedUser[];
  assigneeName?: string | null;
  laneIndex?: number;
  laneCount?: number;
  /** Parent/job lookup for SmartTask-style appointment cards */
  itemsById?: Map<string, WorkItem>;
}

type TimeAllocationDisplayWorkItem = WorkItem & {
  parent_job_number?: string | null;
  parent_title?: string | null;
  custom_fields?: Record<string, unknown> | null;
};

type TimeAllocationRouteContext = {
  route_sequence?: number;
  travel_time_minutes?: number;
  travel_distance_km?: number;
  is_routed?: boolean;
};

function TimeAllocationCard({
  item,
  dragContainerId,
  template,
  priorityBorderColor,
  groupLeaderNamesByGroupKey,
  onViewItem,
  onRefresh,
  isTimeline,
  top,
  height,
  checkForClashes,
  onOpenAppointmentAssignees,
  presetConfig,
  assignedUsers = [],
  assigneeName,
  laneIndex = 0,
  laneCount = 1,
  itemsById,
}: TimeAllocationCardProps) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.mode === 'dark';
  const currentUser = useMemo(() => authService.getUser(), []);
  const canSeeRescheduleStatus = useMemo(
    () => canSeeAppointmentRescheduleState(currentUser),
    [currentUser],
  );

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: buildTimeAllocationCardDragId(dragContainerId || 'default', item.id) });

  const clickStartRef = useRef<{ x: number; y: number; time: number } | null>(null);

  // Resize state — must be declared unconditionally (React rules of hooks)
  // These are only used when isTimeline=true but MUST be called every render.
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStartY, setResizeStartY] = useState(0);
  const [tempHeight, setTempHeight] = useState(height);
  const [pendingResize, setPendingResize] = useState(false);

  // Check if this is a temporary/optimistic item (not yet confirmed by API)
  const isTempItem = String(item.id).startsWith('temp-');

  // Update tempHeight when height prop changes (after refresh)
  useEffect(() => {
    if (!isResizing && !pendingResize) {
      setTempHeight((previous) => (previous === height ? previous : height));
    }
  }, [height, isResizing, pendingResize]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isTimeline ? { top, height } : {}),
  };

  const handleClick = (e: React.MouseEvent) => {
    if (isDragging) return;
    
    if (clickStartRef.current) {
      const dx = Math.abs(e.clientX - clickStartRef.current.x);
      const dy = Math.abs(e.clientY - clickStartRef.current.y);
      const elapsed = Date.now() - clickStartRef.current.time;
      
      // Allow slightly more movement tolerance (10px) to handle small jitters
      if (dx < 10 && dy < 10 && elapsed < 300) {
        e.preventDefault();
        e.stopPropagation();
        // Open the clicked item directly (appointments open as appointments, jobs as jobs)
        onViewItem(item.id);
      }
    }
    clickStartRef.current = null;
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    clickStartRef.current = { x: e.clientX, y: e.clientY, time: Date.now() };
  };

  const formatTimeRange = (item: WorkItem) => {
    if (!item.scheduled_start || !item.scheduled_end) return '';
    return formatTimeRangeSAST(item.scheduled_start, item.scheduled_end);
  };

  const isAppointmentItem = isAppointmentLikeItem(item);
  const displayItem = item as TimeAllocationDisplayWorkItem;
  
  // Get the display number (show both APT + JOB number for appointments)
  const fullDisplayNumber = isAppointmentItem && displayItem.parent_job_number 
    ? `${item.item_number} · ${displayItem.parent_job_number}` 
    : item.item_number;
  const displayNumber = item.item_number || fullDisplayNumber;
  
  // Get display title (parent job title for appointments if available via parent_title, otherwise use item title)
  const displayTitle = isAppointmentItem && displayItem.parent_title 
    ? displayItem.parent_title 
    : item.title;
  const appointmentDisplayNumber = isAppointmentItem
    ? getAppointmentDisplayNumber(item) || null
    : null;
  const appointmentCardTitle = item.title || displayTitle || item.item_number || 'Untitled appointment';
  const routeCustomFields = displayItem.custom_fields;
  const routeContext = routeCustomFields && typeof routeCustomFields === 'object' && routeCustomFields.is_routed
    ? routeCustomFields as TimeAllocationRouteContext
    : null;
  const locationSummary = item.location_name || item.location_address?.split(',')[0]?.trim() || null;
  const appointmentAssignees = useMemo(
    () => (isAppointmentItem
      ? getAppointmentAssignees(item, assignedUsers, assigneeName)
      : []),
    [assignedUsers, assigneeName, isAppointmentItem, item]
  );
  const appointmentAssigneeName = isAppointmentItem
    ? getAppointmentAssigneeName(item, assignedUsers, assigneeName)
    : null;
  const canOpenAppointmentAssignees =
    isAppointmentItem && Boolean(onOpenAppointmentAssignees) && !isTempItem;
  const appointmentIsGroupLeader = isAppointmentItem ? isAppointmentGroupLeader(item) : false;
  const appointmentGroupLeaderName = isAppointmentItem
    ? getAppointmentGroupLeaderDisplayName(item, appointmentAssigneeName)
    : null;

  const currentUserIdForOwnership = useMemo(() => {
    const u = authService.getUser() as { id?: number } | null;
    return typeof u?.id === 'number' ? u.id : null;
  }, []);

  const appointmentOwnership = isAppointmentItem
    ? getWorkItemOwnershipSummary(item as never, currentUserIdForOwnership)
    : null;

  const appointmentOwnershipBadgeClasses = appointmentOwnership?.tone === 'self'
    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
    : appointmentOwnership?.tone === 'other'
      ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
      : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';

  const smartKanbanAppointment = useMemo(() => {
    if (!isAppointmentItem) return null;
    const card = buildSmartTaskAppointmentCard(item as never, {
      context: {},
      itemsById,
      groupNumber: null,
      isGroupLeader: appointmentIsGroupLeader,
      groupLeaderName: appointmentGroupLeaderName,
      assignees: appointmentAssignees,
      compactDescription: routeContext?.route_sequence != null
        ? `Route stop ${routeContext.route_sequence}${routeContext.travel_time_minutes ? ` · ${routeContext.travel_time_minutes} min travel` : ''}`
        : undefined,
      tooltipLines: buildWorkItemTooltipLines(item, { appointmentPrivilegedViewer: canSeeRescheduleStatus }),
    });
    const visibleStatus = item.status || '';
    const statusLabel = formatAppointmentDisplayStatusLabel(visibleStatus);
    const statusTone = getToneForAppointmentStatusKey(visibleStatus);
    // Admin UI: create banner directly to bypass SmartTask logic that collapses resolved/ai_review to "Closed"
    const adminBanner: typeof card.model.banner = statusLabel ? {
      label: statusLabel,
      tone: statusTone,
    } : null;
    return {
      card,
      cardModel: {
        ...card.model,
        banner: adminBanner,
        timingLabel: adminBanner ? null : card.model.timingLabel,
        statusLabel: adminBanner ? null : card.model.statusLabel,
        statusTone,
      },
    };
  }, [
    isAppointmentItem,
    item,
    itemsById,
    appointmentIsGroupLeader,
    appointmentGroupLeaderName,
    appointmentAssignees,
    routeContext,
    canSeeRescheduleStatus,
  ]);

  const stackedCardBaseClasses = presetConfig?.card
    ? `${presetConfig.card.background} ${presetConfig.card.border} ${presetConfig.card.borderRadius} ${presetConfig.card.shadow} ${presetConfig.card.priorityBorderWidth || 'border-l-[3px]'}`
    : 'bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 border-l-[3px]';
  const stackedCardHoverClasses = presetConfig?.card
    ? `${presetConfig.card.backgroundHover} ${presetConfig.card.shadowHover}`
    : 'hover:shadow-md hover:bg-gray-50 dark:hover:bg-gray-700';
  const timelineCardBaseClasses = presetConfig?.card
    ? `${presetConfig.card.background} ${presetConfig.card.border} ${presetConfig.card.borderRadius} ${presetConfig.card.shadow} ${presetConfig.card.priorityBorderWidth || 'border-l-[3px]'} ${presetConfig.card.backgroundHover} ${presetConfig.card.shadowHover}`
    : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 border-l-[3px] rounded shadow-sm hover:shadow-md';
  const cardItemNumberClasses = presetConfig?.card
    ? `${presetConfig.card.itemNumberBg} ${presetConfig.card.itemNumberColor}`
    : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400';
  const cardTitleClasses = presetConfig?.card
    ? `${presetConfig.card.titleSize} ${presetConfig.card.titleWeight} ${presetConfig.card.titleColor}`
    : 'text-sm font-medium text-gray-900 dark:text-white';
  const cardMetaClasses = `${presetConfig?.card?.metaSize || 'text-xs'} ${presetConfig?.card?.metaColor || 'text-gray-500 dark:text-gray-400'}`;
  const progressPercentage = item.progress_percentage ?? 0;
  const lanePlacement = getTimeAllocationLanePlacement(laneIndex, laneCount);

  // Resize handlers — defined unconditionally but only used in timeline mode
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    if (!isTimeline) return;
    // Prevent resize on temp items that haven't been confirmed by API yet
    if (isTempItem) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeStartY(e.clientY);
    setTempHeight(height || 60);
  }, [isTimeline, isTempItem, height]);

  const handleResizeMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;
    const deltaY = e.clientY - resizeStartY;
    const newHeight = Math.max(30, Math.min(14 * 60, (height || 60) + deltaY));
    setTempHeight(newHeight);
  }, [isResizing, resizeStartY, height]);

  const handleResizeEnd = useCallback(async (e: MouseEvent) => {
    if (!isResizing) return;
    
    const finalTempHeight = tempHeight;
    setIsResizing(false);
    
    const deltaY = e.clientY - resizeStartY;
    const HOUR_HEIGHT = 120;
    const hoursDelta = Math.round(deltaY / HOUR_HEIGHT * 2) / 2;
    
    if (Math.abs(hoursDelta) < 0.25) {
      setTempHeight(height);
      return;
    }
    
    setTempHeight(finalTempHeight);
    
    const currentEnd = new Date(item.scheduled_end!);
    const newEnd = new Date(currentEnd.getTime() + hoursDelta * 60 * 60 * 1000);
    const currentStart = new Date(item.scheduled_start!);
    
    const assignedToId = typeof item.assigned_to === 'string'
      ? Number.parseInt(item.assigned_to, 10)
      : item.assigned_to;

    if (checkForClashes && assignedToId && hoursDelta > 0) {
      const clashingAppointments = checkForClashes(assignedToId, currentStart, newEnd, item.id);
      if (clashingAppointments.length > 0) {
        const clashList = clashingAppointments.map(clash => 
          `• ${clash.item_number || clash.title}: ${formatTimeSAST(clash.scheduled_start!)} - ${formatTimeSAST(clash.scheduled_end!)}`
        ).join('\n');
        
        const confirmed = await confirm({
          title: 'Appointment Clash Detected',
          message: `Extending this appointment to ${formatTimeSAST(newEnd)} will overlap with:\n\n${clashList}\n\nDo you want to proceed anyway?`,
          variant: 'warning',
          confirmText: 'Extend Anyway',
          cancelText: 'Cancel',
          onConfirm: () => {},
        });
        
        if (!confirmed) {
          setTempHeight(height);
          return;
        }
      }
    }
    
    const newDurationHours = (newEnd.getTime() - currentStart.getTime()) / (1000 * 60 * 60);
    const newCalculatedHeight = newDurationHours * HOUR_HEIGHT;
    
    setPendingResize(true);
    setTempHeight(newCalculatedHeight);
    
    try {
      const getAuthToken = () => {
        try {
          const storedTokens = localStorage.getItem('auth_tokens');
          if (storedTokens) {
            const tokens = JSON.parse(storedTokens);
            return tokens.access_token;
          }
        } catch (error) {
          console.warn('[Resize] Failed to load auth token:', error);
        }
        return null;
      };

      const token = getAuthToken();
      const response = await fetch(`/api/v1/work-items/${item.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
          scheduled_end: formatLocalISOSAST(newEnd)
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update appointment duration');
      }
      
      if (onRefresh) {
        await onRefresh();
      }
      setPendingResize(false);
    } catch (error) {
      console.error('[Resize] Error:', error);
      notify.error('Failed to update appointment duration');
      setTempHeight(height);
      setPendingResize(false);
    }
  }, [isResizing, resizeStartY, height, tempHeight, item.id, item.scheduled_start, item.scheduled_end, item.assigned_to, checkForClashes, onRefresh]);

  // Attach resize mouse listeners when resizing (unconditional hook)
  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', handleResizeMove);
      window.addEventListener('mouseup', handleResizeEnd);
      return () => {
        window.removeEventListener('mousemove', handleResizeMove);
        window.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  const displayHeight = (isResizing || pendingResize) ? tempHeight : height;

  if (isTimeline) {
    const resizeStyle = {
      ...style,
      ...(isTimeline
        ? {
            top,
            height: displayHeight,
            left: lanePlacement.left,
            width: lanePlacement.width,
            zIndex: laneCount > 1 ? laneIndex + 2 : undefined,
          }
        : {}),
    };

    if (isAppointmentItem) {
      const compact = (displayHeight ?? 0) < 96 || laneCount > 1;
      const ultraCompact = (displayHeight ?? 0) < 68 || laneCount > 2;

      if (!smartKanbanAppointment) {
        return null;
      }

      const travelFooter = routeContext?.travel_time_minutes || routeContext?.travel_distance_km
        ? (
            <div className="text-[11px] text-gray-600 dark:text-gray-400">
              {routeContext?.travel_time_minutes ? `Travel ${routeContext.travel_time_minutes} min` : null}
              {routeContext?.travel_time_minutes && routeContext?.travel_distance_km ? ' · ' : null}
              {routeContext?.travel_distance_km ? `${routeContext.travel_distance_km.toFixed(1)} km` : null}
            </div>
          )
        : null;

      return (
        <div
          ref={setNodeRef}
          style={resizeStyle}
          className={`absolute overflow-visible select-none ${isDragging ? 'opacity-0' : ''} ${isResizing ? 'z-20' : ''}`}
        >
          <div
            {...attributes}
            {...listeners}
            onMouseDown={handleMouseDown}
            onClick={handleClick}
            className="flex h-full min-h-0 flex-col overflow-visible"
          >
            <AppointmentCard
              appointment={smartKanbanAppointment.cardModel}
              compact={compact}
              ultraCompact={ultraCompact}
              presentation="timeline"
              surfaceMode="theme"
              interactive={!isTempItem}
              enableHoverCard
              hideBanner
              omitDefaultBorder
              className={`h-full min-h-0 flex flex-col rounded-2xl text-left shadow-[0_12px_32px_rgba(15,23,42,0.06)] transition-all dark:shadow-[0_18px_40px_rgba(2,6,23,0.32)] supports-[backdrop-filter]:backdrop-blur-xl ${smartKanbanAppointment.card.cardClassName} ${isTempItem ? 'opacity-70' : ''}`}
              contentClassName={ultraCompact ? 'min-h-0 flex-1 overflow-hidden p-2 pb-2 pr-2' : compact ? 'min-h-0 flex-1 overflow-hidden px-2.5 py-2 pb-2.5 pr-2' : 'min-h-0 flex-1 overflow-hidden px-3 py-2 pb-2.5 pr-2'}
              actions={(
                <>
                  {routeContext?.route_sequence != null && !compact ? (
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                      title={`Route stop #${routeContext.route_sequence}`}
                    >
                      🛣️ {routeContext.route_sequence}
                    </span>
                  ) : null}
                  {canOpenAppointmentAssignees ? (
                    <button
                      type="button"
                      onPointerDown={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenAppointmentAssignees?.(item);
                      }}
                      className="rounded p-1 text-amber-700 transition-colors hover:bg-amber-100 dark:text-amber-300 dark:hover:bg-amber-900/30"
                      title="Add or remove assignees"
                    >
                      <Users className="w-3 h-3" />
                    </button>
                  ) : null}
                  <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-slate-400 dark:text-slate-500" />
                </>
              )}
              footer={
                ultraCompact
                  ? travelFooter
                  : (
                      <div className="space-y-1.5">
                        <div className="flex flex-wrap items-center gap-2 text-[10px] text-slate-500 dark:text-slate-400">
                          {item.location_name ? (
                            <span className="flex min-w-0 items-center gap-1 truncate">
                              <MapPin className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate">{item.location_name}</span>
                            </span>
                          ) : item.contact_name ? (
                            <span className="flex min-w-0 items-center gap-1 truncate">
                              <User className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate">{item.contact_name}</span>
                            </span>
                          ) : null}
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          {appointmentOwnership ? (
                            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${appointmentOwnershipBadgeClasses}`}>
                              <User className="h-3 w-3" />
                              {appointmentOwnership.label}
                            </span>
                          ) : null}
                          {item.item_type ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                              {React.createElement(KANBAN_ITEM_TYPE_ICONS[item.item_type] ?? Briefcase, { className: 'h-3 w-3' })}
                              {item.item_type.replace(/_/g, ' ')}
                            </span>
                          ) : null}
                        </div>
                        {travelFooter}
                      </div>
                    )
              }
            />
          </div>

          {isTempItem ? (
            <div className="pointer-events-none absolute right-2 top-2 z-20">
              <div className="h-2 w-2 rounded-full bg-blue-500 animate-ping" />
            </div>
          ) : null}

          <div
            data-resize-handle="bottom"
            onMouseDown={handleResizeStart}
            className={`absolute bottom-0 left-0 right-0 z-40 flex h-3 cursor-ns-resize items-end justify-center rounded-b-2xl border-t border-blue-400/25 bg-gradient-to-t from-blue-500/15 to-transparent pb-0.5 transition-colors dark:border-blue-500/30 dark:from-blue-500/25 ${isResizing ? 'from-blue-500/35' : 'hover:from-blue-500/25'}`}
            title="Drag bottom to extend or shorten duration"
          >
            <div className="flex flex-col items-center gap-0.5 pb-0.5">
              <div className="h-0.5 w-7 rounded-full bg-slate-400/90 dark:bg-slate-500" />
              <div className="h-0.5 w-7 rounded-full bg-slate-400/70 dark:bg-slate-500/80" />
            </div>
          </div>
          <div
            data-resize-handle="right"
            onMouseDown={handleResizeStart}
            className={`absolute bottom-3 right-0 top-12 z-40 w-3 cursor-ns-resize border-l border-blue-400/20 bg-gradient-to-l from-blue-500/10 to-transparent transition-colors hover:from-blue-500/20 dark:border-blue-500/25 dark:from-blue-500/15 ${isResizing ? 'from-blue-500/25' : ''}`}
            title="Drag up or down on the right edge to change duration"
          >
            <div className="absolute right-0.5 top-1/2 h-8 w-0.5 -translate-y-1/2 rounded-full bg-slate-400/80 dark:bg-slate-500" />
          </div>
        </div>
      );
    }
    
    return (
      <WorkItemHoverCard
        as="div"
        workItem={item}
        ref={setNodeRef}
        style={resizeStyle}
        {...attributes}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
        className={`absolute ${timelineCardBaseClasses} ${priorityBorderColor} cursor-pointer transition-all overflow-hidden select-none group ${isDragging ? 'opacity-0' : ''} ${isResizing ? 'ring-2 ring-blue-500' : ''} ${isTempItem ? 'opacity-70 animate-pulse' : ''}`}
      >
        {/* Saving indicator for temp items */}
        {isTempItem && (
          <div className="absolute top-0.5 right-0.5 z-10">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-ping" />
          </div>
        )}
        <div {...listeners} className="p-2.5 h-full flex flex-col gap-1">
          {/* Row 1: Item number + template badge + Group/Lock buttons */}
          <div className="flex min-w-0 items-center justify-between gap-1 overflow-hidden">
            <div className="flex items-center gap-1.5 min-w-0 flex-1">
              <span className={`text-xs font-mono px-1.5 py-0.5 rounded flex-shrink-0 ${cardItemNumberClasses}`}>
                <span className="block max-w-[10rem] truncate">
                  {isTempItem ? '⏳' : displayNumber}
                </span>
              </span>
              {template && (
                <span 
                  className="min-w-0 max-w-[8rem] truncate text-[10px] px-1.5 py-0.5 rounded text-white font-medium flex-shrink"
                  style={{ backgroundColor: template.color || '#6B7280' }}
                >
                  {template.template_name}
                </span>
              )}
              {canOpenAppointmentAssignees && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenAppointmentAssignees?.(item);
                  }}
                  className="flex-shrink-0 rounded p-1 text-amber-700 transition-colors hover:bg-amber-100 dark:text-amber-300 dark:hover:bg-amber-900/30"
                  title="Add or remove assignees"
                >
                  <Users className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
          
          {/* Row 2: Status + En Route badges */}
          <div className="flex flex-wrap items-center gap-1">
            {item.status === 'en_route' && (
              <span className="text-[11px] px-1.5 py-0.5 rounded font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 flex items-center gap-0.5 animate-pulse">
                🚗 En Route
              </span>
            )}
            {item.status === 'arrived' && (
              <span className="text-[11px] px-1.5 py-0.5 rounded font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400">
                📍 Arrived
              </span>
            )}
            {item.status && item.status !== 'open' && item.status !== 'scheduled' && item.status !== 'en_route' && item.status !== 'arrived' && (
              <span className={`text-[11px] px-1.5 py-0.5 rounded font-medium ${
                item.status === 'completed' || item.status === 'closed' 
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  : item.status === 'in_progress' || item.status === 'active'
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                    : item.status === 'pending_closure' || item.status === 'review'
                      ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                      : item.status === 'cancelled' || item.status === 'blocked'
                        ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
              }`}>
                {item.status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </span>
            )}
          </div>
          
          {/* Row 3: Title - always shown */}
          <div className={`${cardTitleClasses} line-clamp-2 leading-snug flex-1`}>
            {template?.icon && <span className="mr-1">{template.icon}</span>}
            {displayTitle}
          </div>
          
          {/* Row 4: Time range - shown when height >= 72px */}
          {displayHeight && displayHeight >= 72 && (
            <div className={`${cardMetaClasses} truncate flex items-center gap-1`}>
              <Calendar className="w-3 h-3 flex-shrink-0" />
              {formatTimeRange(item)}
            </div>
          )}
          
          {/* Row 5: Contact/Customer - shown when height >= 96px */}
          {displayHeight && displayHeight >= 96 && item.contact_name && (
            <div className={`${cardMetaClasses} truncate flex items-center gap-1`}>
              <User className="w-3 h-3 text-gray-400 flex-shrink-0" />
              {item.contact_name}
            </div>
          )}
          
          {/* Row 6: Location - shown when height >= 120px */}
          {displayHeight && displayHeight >= 120 && item.location_address && (
            <div className={`${cardMetaClasses} truncate flex items-center gap-1`}>
              <MapPin className="w-3 h-3 text-gray-400 flex-shrink-0" />
              {item.location_address.split(',')[0]}
            </div>
          )}
          
          {/* Row 7: Description - shown when height >= 150px */}
          {displayHeight && displayHeight >= 150 && item.description && (
            <div className={`${cardMetaClasses} line-clamp-2 leading-tight`}>
              {item.description}
            </div>
          )}
          
          {/* Row 8: Progress bar - shown when height >= 190px and progress > 0 */}
          {displayHeight && displayHeight >= 190 && progressPercentage > 0 && (
            <div className="mt-auto">
              <div className="flex items-center gap-1">
                <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full ${
                      progressPercentage === 100 ? 'bg-gradient-to-r from-green-500 to-emerald-500' :
                      progressPercentage >= 75 ? 'bg-gradient-to-r from-blue-500 to-cyan-500' :
                      progressPercentage >= 50 ? 'bg-gradient-to-r from-amber-500 to-yellow-500' :
                      'bg-gradient-to-r from-orange-500 to-amber-500'
                    }`}
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>
                <span className={cardMetaClasses}>{progressPercentage}%</span>
              </div>
            </div>
          )}
        </div>
        
        {/* Resize: bottom + right edge (same duration logic) */}
        <div
          data-resize-handle="bottom"
          onMouseDown={handleResizeStart}
          className={`absolute bottom-0 left-0 right-0 z-40 flex h-3 cursor-ns-resize items-end justify-center rounded-b-2xl border-t border-blue-400/25 bg-gradient-to-t from-blue-500/15 to-transparent pb-0.5 transition-colors dark:border-blue-500/30 dark:from-blue-500/25 ${isResizing ? 'from-blue-500/35' : 'hover:from-blue-500/25 group-hover:from-blue-500/20'}`}
          title="Drag bottom to extend or shorten duration"
        >
          <div className="flex flex-col items-center gap-0.5 pb-0.5">
            <div className="h-0.5 w-7 rounded-full bg-slate-400/90 dark:bg-slate-500" />
            <div className="h-0.5 w-7 rounded-full bg-slate-400/70 dark:bg-slate-500/80" />
          </div>
        </div>
        <div
          data-resize-handle="right"
          onMouseDown={handleResizeStart}
          className={`absolute bottom-3 right-0 top-12 z-40 w-3 cursor-ns-resize border-l border-blue-400/20 bg-gradient-to-l from-blue-500/10 to-transparent transition-colors hover:from-blue-500/20 dark:border-blue-500/25 dark:from-blue-500/15 ${isResizing ? 'from-blue-500/25' : ''}`}
          title="Drag on the right edge to change duration"
        >
          <div className="absolute right-0.5 top-1/2 h-8 w-0.5 -translate-y-1/2 rounded-full bg-slate-400/80 dark:bg-slate-500" />
        </div>
      </WorkItemHoverCard>
    );
  }

  if (isAppointmentItem) {
    if (!smartKanbanAppointment) {
      return null;
    }

    const stackedTravelFooter = routeContext?.travel_time_minutes || routeContext?.travel_distance_km
      ? (
          <div className="text-[11px] text-gray-600 dark:text-gray-400">
            {routeContext?.travel_time_minutes ? `Travel ${routeContext.travel_time_minutes} min` : null}
            {routeContext?.travel_time_minutes && routeContext?.travel_distance_km ? ' · ' : null}
            {routeContext?.travel_distance_km ? `${routeContext.travel_distance_km.toFixed(1)} km` : null}
          </div>
        )
      : null;

    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
        className={`group overflow-visible select-none ${isDragging ? 'opacity-0' : ''}`}
      >
        <AppointmentCard
          appointment={smartKanbanAppointment.cardModel}
          compact
          surfaceMode="theme"
          interactive={!isTempItem}
          enableHoverCard
          omitDefaultBorder
          className={`w-full cursor-pointer rounded-2xl text-left transition-all active:scale-[0.98] hover:-translate-y-0.5 ${smartKanbanAppointment.card.cardClassName} shadow-[0_12px_32px_rgba(15,23,42,0.06)] transition-all dark:shadow-[0_18px_40px_rgba(2,6,23,0.32)] supports-[backdrop-filter]:backdrop-blur-xl ${stackedCardHoverClasses} ${isTempItem ? 'opacity-70' : ''}`}
          contentClassName="px-3 py-2 overflow-visible"
          actions={(
            <>
              {canOpenAppointmentAssignees ? (
                <button
                  type="button"
                  onPointerDown={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenAppointmentAssignees?.(item);
                  }}
                  className="rounded p-1 text-amber-700 transition-colors hover:bg-amber-100 dark:text-amber-300 dark:hover:bg-amber-900/30"
                  title="Add or remove assignees"
                >
                  <Users className="w-3 h-3" />
                </button>
              ) : null}
              <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-slate-400 dark:text-slate-500" />
            </>
          )}
          footer={(
            <div className="space-y-1.5">
              <div className="flex flex-wrap items-center gap-2 text-[10px] text-slate-500 dark:text-slate-400">
                {item.location_name ? (
                  <span className="flex min-w-0 items-center gap-1 truncate">
                    <MapPin className="h-3 w-3 flex-shrink-0" />
                    <span className="truncate">{item.location_name}</span>
                  </span>
                ) : item.contact_name ? (
                  <span className="flex min-w-0 items-center gap-1 truncate">
                    <User className="h-3 w-3 flex-shrink-0" />
                    <span className="truncate">{item.contact_name}</span>
                  </span>
                ) : null}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {appointmentOwnership ? (
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${appointmentOwnershipBadgeClasses}`}>
                    <User className="h-3 w-3" />
                    {appointmentOwnership.label}
                  </span>
                ) : null}
                {item.item_type ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                    {React.createElement(KANBAN_ITEM_TYPE_ICONS[item.item_type] ?? Briefcase, { className: 'h-3 w-3' })}
                    {item.item_type.replace(/_/g, ' ')}
                  </span>
                ) : null}
              </div>
              {stackedTravelFooter}
            </div>
          )}
        />

        {isTempItem ? (
          <div className="pointer-events-none absolute right-2 top-2 z-20">
            <div className="h-2 w-2 rounded-full bg-blue-500 animate-ping" />
          </div>
        ) : null}
      </div>
    );
  }

  // Stacked (unscheduled) card
  return (
    <WorkItemHoverCard
      as="div"
      workItem={item}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      className={`
        group ${stackedCardBaseClasses} ${priorityBorderColor}
        p-2 cursor-pointer transition-all ${stackedCardHoverClasses}
        select-none
        ${isDragging ? 'opacity-0' : ''} ${isTempItem ? 'opacity-70 animate-pulse' : ''}
      `}
    >
      {isTempItem && (
        <div className="absolute top-1 right-1 z-10">
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-ping" />
        </div>
      )}

      <div className="flex items-start gap-2">
        <GripVertical className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400 dark:text-gray-600" />

        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-1 min-w-0">
            {template?.icon && (
              <span className="text-xs leading-none mt-0.5 flex-shrink-0">{template.icon}</span>
            )}

            <span className={`text-xs font-semibold truncate min-w-0 flex-shrink max-w-[130px] ${presetConfig?.card?.itemNumberColor || 'text-blue-600 dark:text-blue-400'}`}>
              {isTempItem ? `⏳ ${displayNumber}` : displayNumber}
            </span>

            {canOpenAppointmentAssignees && (
              <button
                type="button"
                onPointerDown={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenAppointmentAssignees?.(item);
                }}
                className="flex-shrink-0 rounded p-1 text-amber-700 transition-colors hover:bg-amber-100 dark:text-amber-300 dark:hover:bg-amber-900/30"
                title="Add or remove assignees"
              >
                <Users className="w-3 h-3" />
              </button>
            )}

            {routeContext?.route_sequence != null && (
              <span
                className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400 flex-shrink-0"
                title={`Route stop #${routeContext.route_sequence}${routeContext.travel_time_minutes ? ` — ${routeContext.travel_time_minutes} min travel` : ''}${routeContext.travel_distance_km ? ` (${routeContext.travel_distance_km.toFixed(1)} km)` : ''}`}
              >
                🛣️ {routeContext.route_sequence}
              </span>
            )}

            <span className="flex-1" />
          </div>

          <div className={`${cardTitleClasses} line-clamp-2 leading-snug mt-0.5`}>
            {displayTitle}
          </div>

          {item.scheduled_start && item.scheduled_end && (
            <div className={`${cardMetaClasses} truncate mt-1 flex items-center gap-1`}>
              <Calendar className="w-3 h-3 flex-shrink-0" />
              {formatTimeRange(item)}
            </div>
          )}

          {item.contact_name && (
            <div className={`${cardMetaClasses} truncate mt-1 flex items-center gap-1`}>
              <User className="w-3 h-3 text-gray-400 flex-shrink-0" />
              {item.contact_name}
            </div>
          )}

          {locationSummary && (
            <div className={`${cardMetaClasses} truncate mt-0.5 flex items-center gap-1`}>
              <MapPin className="w-3 h-3 text-gray-400 flex-shrink-0" />
              {locationSummary}
            </div>
          )}

          {item.description && !item.contact_name && !locationSummary && (
            <div className={`${cardMetaClasses} line-clamp-2 leading-tight mt-1`}>
              {item.description}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-1 mt-1.5">
            {item.status === 'en_route' && (
              <span className="text-[9px] px-1.5 py-0.5 rounded font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 flex items-center gap-0.5 animate-pulse">
                🚗 En Route
              </span>
            )}
            {item.status === 'arrived' && (
              <span className="text-[9px] px-1.5 py-0.5 rounded font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400">
                📍 Arrived
              </span>
            )}
            {item.status && !['open', 'scheduled', 'backlog', 'en_route', 'arrived'].includes(item.status) && (
              <span className={`text-[9px] px-1 py-0.5 rounded font-medium ${
                item.status === 'completed' || item.status === 'closed'
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  : item.status === 'in_progress' || item.status === 'active'
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                    : item.status === 'pending_closure' || item.status === 'review'
                      ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                      : item.status === 'cancelled' || item.status === 'blocked'
                        ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
              }`}>
                {item.status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </span>
            )}

            {(item.assigned_to || (item.assignees && item.assignees.length > 0)) && item.item_type === 'appointment' && (
              <span className="text-[9px] px-1 py-0.5 rounded font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                ✓ Assigned
              </span>
            )}
          </div>
        </div>
      </div>
    </WorkItemHoverCard>
  );
}

// ============================================================================
// TIME ALLOCATION VIEW COMPONENT
// Same as Assignment view but with hourly time slots
// Uses exact same columns and itemsByColumn as Assignment view
// ============================================================================

export interface TimeAllocationViewProps {
  columns: WorkflowState[];           // Same columns as Assignment view
  itemsByColumn: Record<string, WorkItem[]>;  // Same grouping as Assignment view
  selectedDate: Date;
  onViewItem: (itemId: string) => void;
  onRefresh?: () => Promise<void>;
  getTemplate: (item: WorkItem) => WorkItemTemplate | null;
  loadingUsers: boolean;
  zoomLevel: ZoomLevel;               // Use same zoom level as other views
  scrollRef?: React.RefObject<HTMLDivElement | null>; // Ref for scroll container synchronization
  onScroll?: () => void; // Scroll handler for synchronization
  columnOrder: string[]; // Custom column order
  hoveredHour: { columnKey: string; hour: number } | null; // Track hovered hour for visual feedback
  checkForClashes?: (userId: number, newStart: Date, newEnd: Date, excludeItemId?: string) => WorkItem[];
  /** Add people to the same appointment so it appears on their schedules */
  onOpenAppointmentAssignees?: (item: WorkItem) => void;
  visibleStartHour: number; // Start hour of visible window (0-23, supports rolling)
  visibleHoursCount: number; // Number of hours visible in the window (default 13)
  defaultVisibleStartHour: number; // Saved default start hour used by reset controls
  onRollHours: (direction: 'up' | 'down') => void; // Roll hours up (earlier) or down (later)
  onSetVisibleStartHour: (hour: number) => void; // Directly set visible start hour
  isTimeSlotInPast: (hour: number, dayOffset?: number) => boolean; // Function to check if a time slot is in the past
  userLeaveData?: UserLeave[]; // Approved leave for visible users
  presetConfig?: KanbanStylePreset; // Kanban style preset configuration (January 2026)
  assignedUsers?: AssignedUser[]; // Users for avatar display in column headers
  onOpenTeamMember?: (userId: number) => void; // Open team member profile modal
  /** All work items (jobs + appointments) for the unified unscheduled sidebar */
  allItems?: WorkItem[];
  hideTimeWindowControls?: boolean;
}

// Generate hour slots for rolling 24-hour view
// Supports crossing midnight - hours > 23 show as next day, hours < 0 show as previous day
function generateRollingHourSlots(startHour: number, hoursCount: number): { hour: number; displayHour: number; label: string; dayOffset: number }[] {
  const slots: { hour: number; displayHour: number; label: string; dayOffset: number }[] = [];
  for (let i = 0; i < hoursCount; i++) {
    const rawHour = startHour + i;
    let displayHour = rawHour;
    let dayOffset = 0;
    
    // Handle crossing midnight forwards (24:00 -> 00:00 next day)
    if (rawHour >= 24) {
      displayHour = rawHour - 24;
      dayOffset = 1;
    }
    // Handle crossing midnight backwards (negative hours -> previous day)
    else if (rawHour < 0) {
      displayHour = rawHour + 24;
      dayOffset = -1;
    }
    
    const label = `${displayHour.toString().padStart(2, '0')}:00`;
    slots.push({ hour: rawHour, displayHour, label, dayOffset });
  }
  return slots;
}

// Legacy function for backwards compatibility
function generateHourSlots(startHour: number = 6, endHour: number = 20): { hour: number; label: string }[] {
  const slots: { hour: number; label: string }[] = [];
  for (let hour = startHour; hour <= endHour; hour++) {
    slots.push({ hour, label: `${hour.toString().padStart(2, '0')}:00` });
  }
  return slots;
}

export default function TimeAllocationView({
  columns,
  itemsByColumn,
  selectedDate,
  onViewItem,
  onRefresh,
  getTemplate,
  loadingUsers,
  zoomLevel,
  scrollRef,
  onScroll,
  columnOrder,
  hoveredHour,
  checkForClashes,
  onOpenAppointmentAssignees,
  visibleStartHour,
  visibleHoursCount,
  defaultVisibleStartHour,
  onRollHours,
  onSetVisibleStartHour,
  isTimeSlotInPast,
  userLeaveData = [],
  isMobile = false,
  presetConfig,
  assignedUsers = [],
  onOpenTeamMember,
  allItems = [],
  hideTimeWindowControls = false,
}: TimeAllocationViewProps & { isMobile?: boolean }) {
  // Rolling hour window - generate hour slots based on visible range
  const hourSlots = useMemo(() => generateRollingHourSlots(visibleStartHour, visibleHoursCount), [visibleStartHour, visibleHoursCount]);

  const workItemsById = useMemo(() => {
    const map = new Map<string, WorkItem>();
    const add = (wi: WorkItem) => {
      map.set(String(wi.id), wi);
    };
    allItems.forEach(add);
    Object.values(itemsByColumn).forEach((col) => col.forEach(add));
    return map;
  }, [allItems, itemsByColumn]);

  const HOUR_HEIGHT = 120;
  
  // For positioning calculations - use the visible window start
  const START_HOUR = visibleStartHour;
  const END_HOUR = visibleStartHour + visibleHoursCount;
  
  // Check if selected date is today (for past-time greying out)
  const isToday = useMemo(() => {
    const today = new Date();
    return selectedDate.getDate() === today.getDate() && 
           selectedDate.getMonth() === today.getMonth() && 
           selectedDate.getFullYear() === today.getFullYear();
  }, [selectedDate]);
  
  // Check if selected date is in the past (before today)
  const isSelectedDateInPast = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Start of today
    const selected = new Date(selectedDate);
    selected.setHours(0, 0, 0, 0); // Start of selected date
    return selected.getTime() < today.getTime();
  }, [selectedDate]);
  
  // isTimeSlotInPast is now passed as a prop from the main component

  const [currentTimeIndicatorDate, setCurrentTimeIndicatorDate] = useState(() => new Date());

  useEffect(() => {
    const tick = () => setCurrentTimeIndicatorDate(new Date());
    tick();
    const interval = window.setInterval(tick, 30_000);
    return () => window.clearInterval(interval);
  }, []);
  
  const zoomSettings = ZOOM_SETTINGS[zoomLevel];

  const currentTimeOffsetPx = useMemo(() => {
    if (!isToday) {
      return null;
    }

    const currentHourFraction = getHourFractionSAST(currentTimeIndicatorDate);
    if (currentHourFraction < START_HOUR || currentHourFraction >= END_HOUR) {
      return null;
    }

    return (currentHourFraction - START_HOUR) * HOUR_HEIGHT;
  }, [END_HOUR, HOUR_HEIGHT, START_HOUR, currentTimeIndicatorDate, isToday]);
  
  // ========================================================================
  // UNIFIED UNSCHEDULED SIDEBAR — shows ALL open jobs (like ResourceScheduleTimeline)
  // ========================================================================
  const [sidebarSearchQuery, setSidebarSearchQuery] = useState('');
  const [sidebarStatusFilter, setSidebarStatusFilter] = useState<string>('all');
  const [sidebarPriorityFilter, setSidebarPriorityFilter] = useState<string>('all');
  const [sidebarContactFilter, setSidebarContactFilter] = useState<string>('all');
  const [sidebarSortOrder, setSidebarSortOrder] = useState<ScheduleSidebarSortOrder>('newest');
  const [showSidebar, setShowSidebar] = useState(true);
  const { currentTheme } = useTheme();
  const isDark = currentTheme.mode === 'dark';
  const appointmentGroupLeaderNames = useMemo(
    () => buildAppointmentGroupLeaderNameMap(
      ((allItems.length > 0 ? allItems : Object.values(itemsByColumn).flat()) as WorkItem[]).filter(isAppointmentLikeItem),
      (item) => getAppointmentAssigneeName(item as WorkItem, assignedUsers)
    ),
    [allItems, assignedUsers, itemsByColumn]
  );
  
  // Compute unified unscheduled items from allItems.
  // Jobs with appointments already scheduled for the selected date are excluded
  // so they no longer appear in the sidebar after an appointment is created.
  const unifiedUnscheduledBaseItems = useMemo(() => {
    return getUnscheduledJobsForSelectedDate({
      items: allItems,
      selectedDate,
      excludeJobsWithAppointmentsOnSelectedDate: true,
    });
  }, [allItems, selectedDate]);

  const unifiedUnscheduledContactOptions = useMemo(
    () => getScheduleSidebarContactOptions(unifiedUnscheduledBaseItems),
    [unifiedUnscheduledBaseItems]
  );

  const unifiedUnscheduledItems = useMemo(() => {
    return getUnscheduledJobsForSelectedDate({
      items: allItems,
      selectedDate,
      excludeJobsWithAppointmentsOnSelectedDate: true,
      searchQuery: sidebarSearchQuery,
      statusFilter: sidebarStatusFilter,
      priorityFilter: sidebarPriorityFilter,
      contactFilter: sidebarContactFilter,
      sortOrder: sidebarSortOrder,
    });
  }, [allItems, selectedDate, sidebarSearchQuery, sidebarStatusFilter, sidebarPriorityFilter, sidebarContactFilter, sidebarSortOrder]);
  
  // Droppable for the unified sidebar
  const { setNodeRef: setUnifiedSidebarRef, isOver: unifiedSidebarIsOver } = useDroppable({
    id: 'unscheduled-sidebar',
  });
  
  // Compute which jobs have appointments in EACH specific column.
  // Column timelines still suppress the duplicate job card when an appointment
  // already exists in that specific lane, while the shared unscheduled sidebar
  // keeps the source job available for creating additional same-day visits.
  const jobAppointmentsByColumn = useMemo(() => {
    const columnAppointments: Record<string, Set<string>> = {};
    Object.entries(itemsByColumn).forEach(([columnKey, columnItems]) => {
      columnAppointments[columnKey] = new Set<string>();
      columnItems.forEach(item => {
        if (item.item_type === 'appointment' && item.parent_id) {
          columnAppointments[columnKey].add(item.parent_id);
        }
      });
    });
    return columnAppointments;
  }, [itemsByColumn]);
  
  // Calculate position and height for a scheduled item
  // Supports rolling hour window - items positioned relative to visibleStartHour
  const getItemStyle = (item: WorkItem): { top: number; height: number; isScheduled: boolean; partiallyVisible?: 'top' | 'bottom' | 'both' } => {
    if (!item.scheduled_start || !item.scheduled_end) {
      return { top: 0, height: 0, isScheduled: false };
    }
    
    const itemDate = new Date(item.scheduled_start);
    const startTime = new Date(item.scheduled_start);
    const endTime = new Date(item.scheduled_end);
    
    // Get the date for the visible window (considering day offsets when rolling past midnight)
    const visibleWindowDate = new Date(selectedDate);
    
    // Check if item is on the selected date (or adjacent dates for cross-midnight items)
    const itemStartDay = itemDate.toDateString();
    const selectedDay = selectedDate.toDateString();
    const nextDay = new Date(selectedDate);
    nextDay.setDate(nextDay.getDate() + 1);
    const prevDay = new Date(selectedDate);
    prevDay.setDate(prevDay.getDate() - 1);
    
    // Calculate hour fractions - needs to account for cross-midnight
    let startHourFraction = startTime.getHours() + startTime.getMinutes() / 60;
    let endHourFraction = endTime.getHours() + endTime.getMinutes() / 60;
    
    // Adjust for items on adjacent days when window crosses midnight
    if (itemStartDay === prevDay.toDateString() && START_HOUR < 0) {
      // Item from previous day, visible because we rolled before midnight
      startHourFraction -= 24;
      endHourFraction -= 24;
    } else if (itemStartDay === nextDay.toDateString() && END_HOUR > 24) {
      // Item on next day, visible because we rolled past midnight
      startHourFraction += 24;
      endHourFraction += 24;
    } else if (itemStartDay !== selectedDay) {
      // Item not on selected date and not in view
      return { top: 0, height: 0, isScheduled: false };
    }
    
    // Check if item is within the visible window
    const windowStart = START_HOUR;
    const windowEnd = END_HOUR;
    
    if (endHourFraction <= windowStart || startHourFraction >= windowEnd) {
      // Item is completely outside visible window
      return { top: 0, height: 0, isScheduled: false };
    }
    
    // Clamp to visible window
    const clampedStart = Math.max(startHourFraction, windowStart);
    const clampedEnd = Math.min(endHourFraction, windowEnd);
    const top = (clampedStart - windowStart) * HOUR_HEIGHT;
    const height = Math.max((clampedEnd - clampedStart) * HOUR_HEIGHT, 30);
    
    // Determine if item is partially visible
    let partiallyVisible: 'top' | 'bottom' | 'both' | undefined = undefined;
    if (startHourFraction < windowStart && endHourFraction > windowEnd) {
      partiallyVisible = 'both';
    } else if (startHourFraction < windowStart) {
      partiallyVisible = 'top';
    } else if (endHourFraction > windowEnd) {
      partiallyVisible = 'bottom';
    }
    
    return { top, height, isScheduled: true, partiallyVisible };
  };
  
  const formatTimeRange = (item: WorkItem): string => {
    if (!item.scheduled_start || !item.scheduled_end) return '';
    return formatTimeRangeSAST(item.scheduled_start, item.scheduled_end);
  };
  
  // Unconditional droppable hooks for the unassigned column.
  // These MUST be called at the top level (not inside renderColumn) to avoid
  // conditional hook calls when unassignedColumn or loadingUsers changes.
  const { setNodeRef: setUnassignedUnscheduledRef, isOver: unassignedUnscheduledIsOver } = useDroppable({
    id: 'unassigned-unscheduled',
  });
  const { setNodeRef: setUnassignedTimelineRef, isOver: unassignedTimelineIsOver } = useDroppable({
    id: 'unassigned-timeline',
  });
  
  // Separate Unassigned column and user columns
  // Schedule views should only render actual assignee columns, never synthetic status lanes.
  const unassignedColumn = columns.find(col => col.key === 'unassigned');
  const userColumns = columns.filter(col => col.key.startsWith('user-') || col.key.startsWith('team-'));
  
  // Render a single column (Unassigned only) - user columns use SortableTimeAllocationColumn
  // Droppable hooks are called at the top level and passed in to avoid conditional hook violations
  const renderColumn = (column: WorkflowState, showTimeline: boolean = true) => {
    const columnItems = itemsByColumn[column.key] || [];
    const colorClass = COLOR_CLASSES[column.color] || COLOR_CLASSES.gray;
    const isUnassigned = column.key === 'unassigned';
    const StateIcon = isUnassigned ? UserCheck : User;
    
    // Find user for avatar initials
    const isScheduleUserColumn = column.key.startsWith('user-');
    const isScheduleTeamColumn = column.key.startsWith('team-');
    const scheduleUserId = isScheduleUserColumn ? parseInt(column.key.replace('user-', '')) : null;
    const scheduleUser = scheduleUserId ? assignedUsers.find(u => u.id === scheduleUserId) : null;
    
    // Build column styles from preset or fallback to defaults (matching KanbanColumn)
    const columnStyles = presetConfig?.column;
    
    // Use the top-level droppable refs (unconditionally called above)
    const setUnscheduledRef = setUnassignedUnscheduledRef;
    const setTimelineRef = setUnassignedTimelineRef;
    const unscheduledIsOver = unassignedUnscheduledIsOver;
    const timelineIsOver = unassignedTimelineIsOver;
    
    const isOver = unscheduledIsOver || timelineIsOver;
    
    // Get the appointments for THIS specific column
    const columnJobsWithAppointments = jobAppointmentsByColumn[column.key] || new Set<string>();
    
    // Filter out jobs that have appointments IN THIS COLUMN ONLY
    // Jobs can still appear in other users' columns as unscheduled even if they have appointments elsewhere
    const filteredItems = columnItems.filter(item => {
      // If it's a job with appointments in THIS column, don't show the job card (show the appointment instead)
      if (item.item_type === 'job' && columnJobsWithAppointments.has(item.id)) {
        return false;
      }
      return true;
    });
    
    // Split items into scheduled (for today) and unscheduled
    const scheduledItems: WorkItem[] = [];
    const unscheduledItems: WorkItem[] = [];
    filteredItems.forEach(item => {
      const style = getItemStyle(item);
      if (style.isScheduled) {
        scheduledItems.push(item);
      } else {
        unscheduledItems.push(item);
      }
    });
    const scheduledLayouts = buildOverlapLaneLayout(
      scheduledItems.map((item) => ({
        item,
        itemStyle: getItemStyle(item),
      })),
      {
        getId: (entry) => entry.item.id,
        getStart: (entry) => entry.itemStyle.top,
        getEnd: (entry) => entry.itemStyle.top + Math.max(entry.itemStyle.height, 30),
      }
    );
    const columnItemDragIds = columnItems.map((item) => buildTimeAllocationCardDragId(`${column.key}:stacked`, item.id));
    const unscheduledItemDragIds = unscheduledItems.map((item) => buildTimeAllocationCardDragId(`${column.key}:unscheduled`, item.id));
    const scheduledItemDragIds = scheduledItems.map((item) => buildTimeAllocationCardDragId(`${column.key}:timeline`, item.id));
    
    // Build column container classes from preset or defaults (matching KanbanColumn)
    const columnClasses = columnStyles 
      ? `${columnStyles.background} ${columnStyles.borderRadius} ${columnStyles.border} ${columnStyles.shadow}`
      : `${colorClass.header} rounded-xl border-2`;
    
    return (
      <div
        key={column.key}
        className={`
          flex flex-col overflow-hidden ${zoomSettings.columnWidth} flex-shrink-0 
          ${columnClasses}
          ${isOver ? 'ring-2 ring-blue-500 scale-[1.01] shadow-xl' : (columnStyles ? '' : colorClass.border)}
          transition-all duration-150 ease-out
        `}
        style={{ height: showTimeline ? `${(hourSlots.length * HOUR_HEIGHT) + 280}px` : 'auto' }}
      >
        {/* Column Header - Styled from preset (matching KanbanColumn/Assignment view) */}
        <div className="sticky top-0 z-30">
          <div className={`${zoomSettings.headerPadding} border-b border-gray-200/50 dark:border-gray-600/50 ${columnStyles?.headerBackground || ''} ${getTimeAllocationHeaderRadiusClass(columnStyles)}`}>
            <div className={`flex items-center ${zoomLevel === 'compact' ? 'flex-col gap-1' : 'justify-between mb-2'}`}>
              <div className="flex items-center gap-2">
                {/* Avatar - clickable initials for user columns, icon for unassigned/status columns */}
                {scheduleUser ? (
                  <button
                    type="button"
                    className="cursor-pointer hover:ring-2 hover:ring-orange-400 rounded-full transition-all"
                    onClick={(e) => { e.stopPropagation(); if (onOpenTeamMember && scheduleUserId) onOpenTeamMember(scheduleUserId); }}
                    title={`View ${scheduleUser.first_name} ${scheduleUser.last_name} profile`}
                  >
                    <div className={`${zoomLevel === 'compact' ? 'w-7 h-7' : 'w-9 h-9'} rounded-full flex items-center justify-center text-white ${zoomLevel === 'compact' ? 'text-[9px]' : 'text-xs'} font-semibold shadow-sm ${getKBAvatarColor(scheduleUser.email, scheduleUser.first_name)}`}>
                      {getKBInitials(scheduleUser.first_name, scheduleUser.last_name, scheduleUser.email)}
                    </div>
                  </button>
                ) : (
                  <div className={`p-${zoomLevel === 'compact' ? '1.5' : '2'} rounded-lg ${colorClass.iconBg} text-white shadow-sm`}>
                    <StateIcon className={`${zoomLevel === 'compact' ? 'w-3 h-3' : 'w-4 h-4'}`} />
                  </div>
                )}
                <div>
                  {scheduleUserId ? (
                    <button
                      type="button"
                      className={`font-semibold hover:underline cursor-pointer ${columnStyles?.headerTextColor || colorClass.headingText} ${zoomSettings.headerText}`}
                      onClick={(e) => { e.stopPropagation(); if (onOpenTeamMember) onOpenTeamMember(scheduleUserId); }}
                      title={column.label}
                    >
                      {column.label}
                    </button>
                  ) : (
                    <h3 className={`font-semibold ${columnStyles?.headerTextColor || colorClass.headingText} ${zoomSettings.headerText}`}>{column.label}</h3>
                  )}
                  {column.description && zoomLevel !== 'compact' && (
                    <p className={`text-xs ${colorClass.subtitleText}`}>{column.description}</p>
                  )}
                </div>
              </div>
              <span className={`font-bold ${columnStyles?.countBadge ? `${columnStyles.countBadge.background} ${columnStyles.countBadge.textColor} ${columnStyles.countBadge.borderRadius}` : `${colorClass.headingText} bg-white/50 dark:bg-black/20 rounded-full`} ${zoomSettings.badgeSize} px-2 py-1`}>
                {columnItems.length}
              </span>
            </div>
            
            {/* Send Schedule Button - Only show for user columns (not unassigned) */}
            {!isUnassigned && !isScheduleTeamColumn && showTimeline && (
              <button
              onClick={async (event: React.MouseEvent<HTMLButtonElement>) => {
                const button = event.currentTarget;
                try {
                  // Extract user_id from column key (format: "user-123")
                  const userId = parseInt(column.key.replace('user-', ''));
                  if (isNaN(userId)) {
                    return;
                  }

                  await sendScheduleNotificationToUser({
                    userId,
                    scheduleDate: selectedDate,
                    appointments: scheduledItems,
                  });

                  notify.success('Schedule sent successfully');
                  const originalContent = button.innerHTML;
                  button.innerHTML = '✓ Sent!';
                  button.disabled = true;
                  window.setTimeout(() => {
                    button.innerHTML = originalContent;
                    button.disabled = false;
                  }, 2000);
                } catch (error) {
                  console.error('[Kanban] Error sending schedule:', error);
                  notify.error(`Failed to send schedule: ${error instanceof Error ? error.message : 'Unknown error'}`);
                }
              }}
              className={`mt-2 w-full ${zoomSettings.fontSize} px-2 py-1.5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg shadow-sm hover:shadow-md transition-all flex items-center justify-center gap-1.5`}
              title="Send today's schedule to this user immediately"
              >
                <CalendarClock className="w-3.5 h-3.5" />
                <span className="font-medium">Send Schedule Now</span>
              </button>
            )}
          </div>
        </div>
        
        {/* For Unassigned column - just show stacked cards like Assignment view */}
        {!showTimeline && (
          <div 
            ref={setUnscheduledRef}
            data-column-id={column.key}
            className={`flex-1 ${zoomSettings.columnPadding} space-y-${zoomLevel === 'compact' ? '2' : '3'} overflow-y-auto ${columnStyles ? '' : colorClass.bg}`}
          >
            <SortableContext items={columnItemDragIds} strategy={verticalListSortingStrategy}>
              {columnItems.map((item) => {
                const template = getTemplate(item);
                // Priority border color - same as KanbanCard
                const priorityBorderColor = {
                  urgent: 'border-l-red-600',
                  critical: 'border-l-red-700',
                  high: 'border-l-orange-500',
                  medium: 'border-l-yellow-500',
                  low: 'border-l-green-500',
                }[item.priority || 'medium'] || 'border-l-gray-400';
                
                return (
                  <TimeAllocationCard
                    key={item.id}
                    item={item}
                    dragContainerId={`${column.key}:stacked`}
                    template={template}
                    priorityBorderColor={priorityBorderColor}
                    groupLeaderNamesByGroupKey={appointmentGroupLeaderNames}
                    onViewItem={onViewItem}
                    onRefresh={onRefresh}
                    isTimeline={false}
                    checkForClashes={checkForClashes}
                    presetConfig={presetConfig}
                    assignedUsers={assignedUsers}
                    itemsById={workItemsById}
                  />
                );
              })}
            </SortableContext>
            {columnItems.length === 0 && (
              <div className="text-center py-8 text-gray-400 dark:text-gray-500">
                <StateIcon className={`${zoomLevel === 'compact' ? 'w-8 h-8' : 'w-12 h-12'} mx-auto mb-2 opacity-50`} />
                <p className={zoomSettings.fontSize}>No unassigned items</p>
              </div>
            )}
          </div>
        )}
        
        {/* For User columns - show timeline with scheduled items */}
        {showTimeline && (
          <>
            {/* Unscheduled items section - Fixed height for consistency */}
            <div 
              ref={setUnscheduledRef}
              data-column-id={`${column.key}-unscheduled`}
              data-unscheduled-area={column.key}
              className={`${zoomSettings.columnPadding} space-y-2 border-b-2 ${columnStyles ? 'border-amber-400/60 dark:border-amber-500/60' : 'border-gray-300 dark:border-gray-600'} ${columnStyles ? '' : colorClass.bg}`}
              style={{ height: '180px', overflowY: 'auto' }}
            >
              <div className="text-[10px] font-semibold text-amber-700 dark:text-amber-400 px-1 flex items-center gap-1 sticky top-0 bg-gradient-to-r from-amber-100 to-yellow-100 dark:from-amber-900/50 dark:to-yellow-900/50 z-10 pb-1 rounded-md shadow-sm border border-amber-300 dark:border-amber-700">
                <CalendarOff className="w-3 h-3" />
                Unscheduled Jobs ({unscheduledItems.length})
              </div>
              {unscheduledItems.length > 0 ? (
                <SortableContext items={unscheduledItemDragIds} strategy={verticalListSortingStrategy}>
                  {unscheduledItems.map((item) => {
                    const template = getTemplate(item);
                    const priorityBorderColor = {
                      urgent: 'border-l-red-600',
                      critical: 'border-l-red-700',
                      high: 'border-l-orange-500',
                      medium: 'border-l-yellow-500',
                      low: 'border-l-green-500',
                    }[item.priority || 'medium'] || 'border-l-gray-400';
                    
                    return (
                      <TimeAllocationCard
                        key={item.id}
                        item={item}
                        dragContainerId={`${column.key}:unscheduled`}
                        template={template}
                        priorityBorderColor={priorityBorderColor}
                        groupLeaderNamesByGroupKey={appointmentGroupLeaderNames}
                        onViewItem={onViewItem}
                        onRefresh={onRefresh}
                        isTimeline={false}
                        checkForClashes={checkForClashes}
                        presetConfig={presetConfig}
                        assignedUsers={assignedUsers}
                        itemsById={workItemsById}
                      />
                    );
                  })}
                </SortableContext>
              ) : (
                <div className="text-center py-4 text-gray-400 dark:text-gray-500 text-xs">
                  No unscheduled jobs
                </div>
              )}
            </div>
            
            {/* Time slots with scheduled items - Make this area droppable */}
            <div 
              ref={setTimelineRef}
              data-column-id={`${column.key}-timeline`}
              data-timeline-area={column.key}
              className={`relative`} 
              style={{ height: hourSlots.length * HOUR_HEIGHT }}
            >
              {/* Hour grid lines with alternating shading and inline hour labels */}
              {hourSlots.map((slot, index) => {
                const isEven = index % 2 === 0;
                const isPast = isTimeSlotInPast(slot.displayHour, slot.dayOffset);
                
                // Build alternating background from column color (matching SortableTimeAllocationColumn)
                let bgClass = '';
                if (isPast) {
                  bgClass = 'bg-gray-200/70 dark:bg-gray-700/50';
                } else if (column.color === 'blue') {
                  bgClass = isEven ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-blue-100 dark:bg-blue-900/30';
                } else if (column.color === 'purple') {
                  bgClass = isEven ? 'bg-purple-50 dark:bg-purple-900/20' : 'bg-purple-100 dark:bg-purple-900/30';
                } else if (column.color === 'green') {
                  bgClass = isEven ? 'bg-green-50 dark:bg-green-900/20' : 'bg-green-100 dark:bg-green-900/30';
                } else if (column.color === 'orange') {
                  bgClass = isEven ? 'bg-orange-50 dark:bg-orange-900/20' : 'bg-orange-100 dark:bg-orange-900/30';
                } else if (column.color === 'red') {
                  bgClass = isEven ? 'bg-red-50 dark:bg-red-900/20' : 'bg-red-100 dark:bg-red-900/30';
                } else if (column.color === 'yellow') {
                  bgClass = isEven ? 'bg-yellow-50 dark:bg-yellow-900/20' : 'bg-yellow-100 dark:bg-yellow-900/30';
                } else {
                  bgClass = isEven ? 'bg-gray-50 dark:bg-gray-800/50' : 'bg-gray-100 dark:bg-gray-800/70';
                }
                
                // Check if this hour is being hovered over during drag
                const isHovered = hoveredHour?.columnKey === column.key && hoveredHour?.hour === slot.hour;
                
                return (
                  <div
                    key={slot.hour}
                    className={`absolute w-full border-b border-gray-200/30 dark:border-gray-700/30 transition-all duration-100 ${bgClass} ${
                      isHovered && !isPast ? 'ring-4 ring-blue-400 ring-inset bg-blue-100/60 dark:bg-blue-900/60 z-10' : ''
                    } ${isPast ? 'cursor-not-allowed' : ''}`}
                    style={{ top: (slot.hour - START_HOUR) * HOUR_HEIGHT, height: HOUR_HEIGHT }}
                  >
                    {/* Inline hour label */}
                    <span className={`absolute top-0.5 left-1 text-[10px] font-medium select-none pointer-events-none z-[1] ${
                      isPast ? 'text-gray-400 dark:text-gray-600' : 'text-gray-400 dark:text-gray-500'
                    }`}>
                      {slot.label}
                    </span>
                  </div>
                );
              })}
              {/* Scheduled items - with priority left border like KanbanCard - use rectSortingStrategy to prevent items from moving during drag */}
              <SortableContext items={scheduledItemDragIds} strategy={rectSortingStrategy}>
                {scheduledLayouts.map((layout) => {
                  const { item, itemStyle } = layout.item;
                  const { top, height } = itemStyle;
                  const template = getTemplate(item);
                  const priority = (item.priority || 'medium').toLowerCase();
                  const priorityColors = TIME_ALLOC_PRIORITY_COLORS[priority] || TIME_ALLOC_PRIORITY_COLORS.medium;
                  // Priority border color - same as KanbanCard (3px left border)
                  const priorityBorderColor = {
                    urgent: 'border-l-red-600',
                    critical: 'border-l-red-700',
                    high: 'border-l-orange-500',
                    medium: 'border-l-yellow-500',
                    low: 'border-l-green-500',
                  }[item.priority || 'medium'] || 'border-l-gray-400';
                  
                  return (
                    <TimeAllocationCard
                      key={item.id}
                      item={item}
                      dragContainerId={`${column.key}:timeline`}
                      template={template}
                      priorityBorderColor={priorityBorderColor}
                      onViewItem={onViewItem}
                      onRefresh={onRefresh}
                      isTimeline={true}
                      top={top}
                      height={Math.max(height, 30)}
                      checkForClashes={checkForClashes}
                      onOpenAppointmentAssignees={onOpenAppointmentAssignees}
                      presetConfig={presetConfig}
                      assignedUsers={assignedUsers}
                      assigneeName={column.label}
                      laneIndex={layout.laneIndex}
                      laneCount={layout.laneCount}
                      itemsById={workItemsById}
                    />
                  );
                })}
              </SortableContext>

              {currentTimeOffsetPx !== null && (
                <div
                  className="pointer-events-none absolute inset-x-0 z-30 h-0"
                  style={{ top: `${currentTimeOffsetPx}px` }}
                >
                  <div className={`absolute inset-x-0 -translate-y-1/2 h-[3px] rounded-full ${TIME_ALLOCATION_CURRENT_TIME_LINE_CLASS} shadow-[0_0_14px_rgba(59,130,246,0.45)]`} />
                  <div className="absolute left-1 top-0 h-3 w-3 -translate-y-1/2 rounded-full border-2 border-white bg-blue-500 shadow-md dark:border-slate-900 dark:bg-cyan-300" />
                </div>
              )}
            </div>
          </>
        )}
        {/* Later button - scroll to later hours */}
        {(visibleStartHour + visibleHoursCount < 36) && (
          <button
            onClick={() => onRollHours('down')}
            className="w-full py-1.5 flex items-center justify-center gap-1 text-[10px] font-medium text-orange-500 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/30 transition-colors border-t border-gray-200/50 dark:border-gray-700/50"
          >
            Later
            <ChevronDown className="w-3 h-3" />
          </button>
        )}
        {/* Reset button */}
          {visibleStartHour !== defaultVisibleStartHour && (
          <button
              onClick={() => onSetVisibleStartHour(defaultVisibleStartHour)}
            className="w-full py-1 flex items-center justify-center gap-1 text-[9px] font-medium text-blue-500 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors border-t border-gray-200/50 dark:border-gray-700/50"
          >
            <RotateCcw className="w-2.5 h-2.5" />
            Reset to {`${defaultVisibleStartHour.toString().padStart(2, '0')}:00`}
          </button>
        )}
      </div>
    );
  };
  
  // Apply column order if available
  const sortedUserColumns = useMemo(() => {
    if (columnOrder.length > 0) {
      return [...userColumns].sort((a, b) => {
        const indexA = columnOrder.indexOf(a.key);
        const indexB = columnOrder.indexOf(b.key);
        const orderA = indexA === -1 ? 999 : indexA;
        const orderB = indexB === -1 ? 999 : indexB;
        return orderA - orderB;
      });
    }
    return userColumns;
  }, [userColumns, columnOrder]);

  const sortableUserColumnIds = useMemo(
    () => sortedUserColumns.map((col) => `column-${col.key}`),
    [sortedUserColumns]
  );

  // Time labels column component (extracted for reuse)
  const TimeLabelsColumn = () => {
    // Build time labels column styles from preset
    const columnStyles = presetConfig?.column;
    const timeLabelColumnClasses = columnStyles
      ? `${columnStyles.background} ${columnStyles.borderRadius} ${columnStyles.border} ${columnStyles.shadow}`
      : `rounded-xl ${COLOR_CLASSES.gray.header} border-2 ${COLOR_CLASSES.gray.border}`;
    
    return (
      <div className="flex-shrink-0">
        <div className={`flex-shrink-0 w-16 flex flex-col overflow-hidden ${timeLabelColumnClasses}`}>
          {/* Header */}
          <div className={`${zoomSettings.headerPadding} border-b border-gray-200/50 dark:border-gray-600/50 ${columnStyles?.headerBackground || ''} ${getTimeAllocationHeaderRadiusClass(columnStyles)} flex items-center justify-center`} style={{ minHeight: '62px' }}>
            <div className={`p-2 rounded-lg ${COLOR_CLASSES.gray.iconBg}`}>
              <Clock className="w-4 h-4 text-white" />
            </div>
          </div>
          {/* Hour labels */}
          <div>
            {hourSlots.map((slot, index) => {
              const isEven = index % 2 === 0;
              const isPast = isTimeSlotInPast(slot.displayHour, slot.dayOffset);
              const isDayBoundary = index > 0 && hourSlots[index - 1].dayOffset !== slot.dayOffset;
              
              return (
                <div
                  key={`${slot.hour}-${slot.dayOffset}`}
                  className={`border-b border-gray-200/30 dark:border-gray-700/30 text-xs font-medium flex flex-col items-center justify-start pt-1 ${
                    isEven ? 'bg-gray-50 dark:bg-gray-800/50' : 'bg-white dark:bg-gray-800'
                  } ${
                    isPast 
                      ? 'text-gray-300 dark:text-gray-600 bg-gray-100/80 dark:bg-gray-900/50' 
                      : 'text-gray-500 dark:text-gray-400'
                  } ${
                    isDayBoundary ? 'border-t-2 border-t-orange-400 dark:border-t-orange-500' : ''
                  }`}
                  style={{ height: HOUR_HEIGHT }}
                >
                  <span>{slot.label}</span>
                  {/* Show day indicator for cross-midnight hours */}
                  {slot.dayOffset !== 0 && slot.displayHour === 0 && (
                    <span className="text-[8px] text-orange-500 dark:text-orange-400 font-bold">
                      {slot.dayOffset > 0 ? '+1 day' : '-1 day'}
                    </span>
                  )}
                </div>
              );
            })}
            {/* Reset to default hours button */}
            {visibleStartHour !== defaultVisibleStartHour && (
              <button
                onClick={() => onSetVisibleStartHour(defaultVisibleStartHour)}
                className="w-full py-1 flex items-center justify-center gap-1 text-[9px] text-blue-500 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors border-t border-gray-200 dark:border-gray-700"
                title={`Reset to default hours (${defaultVisibleStartHour.toString().padStart(2, '0')}:00 start)`}
              >
                <RotateCcw className="w-2.5 h-2.5" />
                <span>Reset</span>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };
  
  return loadingUsers ? (
    <div className="flex items-center justify-center w-full py-12">
      <div className="text-center">
        <BrandedLoadingSpinner mode="compact" size="sm" showSurface={false} showProgressBar={false} />
        <p className="text-gray-600">Loading team members...</p>
      </div>
    </div>
  ) : (
    <div className={`flex h-full min-h-0 flex-col overflow-hidden ${presetConfig?.board?.background || ''} ${presetConfig?.board?.borderRadius || ''}`}>
      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* ================================================================
            UNIFIED UNSCHEDULED JOBS SIDEBAR
            Shows ALL open jobs (like ResourceScheduleTimeline sidebar)
            Replaces the old per-user unscheduled sections and Unassigned column
            ================================================================ */}
        {showSidebar && allItems.length > 0 && (
          <div 
            ref={setUnifiedSidebarRef}
            data-unscheduled-area="sidebar"
            className={`${SCHEDULE_SIDEBAR_WIDTH_CLASS} flex-shrink-0 border-r flex flex-col overflow-hidden min-h-0 max-h-full ${
              presetConfig?.column?.background || 'bg-white dark:bg-gray-900'
            } ${isDark ? 'border-gray-700' : 'border-gray-200'} ${
              unifiedSidebarIsOver ? 'ring-2 ring-blue-500' : ''
            }`}
          >
            <ScheduleSidebarShell
              title="Unscheduled Jobs"
              count={unifiedUnscheduledItems.length}
              icon={Briefcase}
              isDark={isDark}
              presetConfig={presetConfig}
              searchPlaceholder="Search jobs..."
              searchQuery={sidebarSearchQuery}
              onSearchQueryChange={setSidebarSearchQuery}
              statusFilter={sidebarStatusFilter}
              onStatusFilterChange={setSidebarStatusFilter}
              priorityFilter={sidebarPriorityFilter}
              onPriorityFilterChange={setSidebarPriorityFilter}
              contactFilter={sidebarContactFilter}
              onContactFilterChange={setSidebarContactFilter}
              contactOptions={unifiedUnscheduledContactOptions}
              sortOrder={sidebarSortOrder}
              onSortOrderChange={setSidebarSortOrder}
            >
            {unifiedUnscheduledItems.length === 0 ? (
              <div className={`text-center py-8 text-sm ${
                isDark ? 'text-gray-500' : 'text-gray-400'
              }`}>
                <Briefcase className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No unscheduled jobs</p>
                {sidebarSearchQuery && (
                  <p className="text-xs mt-1">Try adjusting your search</p>
                )}
              </div>
            ) : (
              <SortableContext items={unifiedUnscheduledItems.map(i => i.id)} strategy={verticalListSortingStrategy}>
                {unifiedUnscheduledItems.map(item => (
                  <ScheduleSidebarItemCard
                    key={item.id}
                    item={item}
                    template={getTemplate(item)}
                    groupLeaderNamesByGroupKey={appointmentGroupLeaderNames}
                    onClick={() => onViewItem(item.id)}
                    isDark={isDark}
                    presetConfig={presetConfig}
                    enableHoverCard={false}
                    dragMode="sortable"
                  />
                ))}
              </SortableContext>
            )}
            </ScheduleSidebarShell>
          </div>
        )}
        
        {/* Scrollable User Columns Container - single scroll area for both axes */}
        <div 
          ref={scrollRef}
          onScroll={onScroll}
          className={`flex-1 min-h-0 overflow-auto ${isMobile ? 'scrollbar-visible' : ''}`}
          style={{ 
            ...(isMobile ? {
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'thin',
              scrollbarColor: '#9CA3AF #E5E7EB',
              paddingBottom: '12px'
            } : {})
          }}
        >
          <div className={`flex ${presetConfig?.board?.columnGap || 'gap-4'}`} style={{ minWidth: 'max-content' }}>
          {/* Wrap user columns in SortableContext for column reordering */}
          <SortableContext 
            items={sortableUserColumnIds}
            strategy={horizontalListSortingStrategy}
          >
            {/* User Columns (with timeline) - wrapped for drag-to-reorder */}
            {sortedUserColumns.map((column) => {
              // Extract user ID from column key (format: user-123)
              const userIdMatch = column.key.match(/^user-(\d+)$/);
              const userId = userIdMatch ? parseInt(userIdMatch[1], 10) : null;
              // Find leave for this user
              const userLeave = userId ? userLeaveData.find(leave => leave.user_id === userId) : undefined;
              
              return (
                <SortableTimeAllocationColumn
                  key={column.key}
                  column={column}
                  itemsByColumn={itemsByColumn}
                  selectedDate={selectedDate}
                  groupLeaderNamesByGroupKey={appointmentGroupLeaderNames}
                  getTemplate={getTemplate}
                  onViewItem={onViewItem}
                  onRefresh={onRefresh}
                  getItemStyle={getItemStyle}
                  hourSlots={hourSlots}
                  zoomLevel={zoomLevel}
                  zoomSettings={zoomSettings}
                  START_HOUR={START_HOUR}
                  HOUR_HEIGHT={HOUR_HEIGHT}
                  hoveredHour={hoveredHour}
                  checkForClashes={checkForClashes}
                  onOpenAppointmentAssignees={onOpenAppointmentAssignees}
                  isTimeSlotInPast={isTimeSlotInPast}
                  userLeave={userLeave}
                  presetConfig={presetConfig}
                  onRollHours={onRollHours}
                  visibleStartHour={visibleStartHour}
                  visibleHoursCount={visibleHoursCount}
                  defaultVisibleStartHour={defaultVisibleStartHour}
                  onSetVisibleStartHour={onSetVisibleStartHour}
                  onOpenTeamMember={onOpenTeamMember}
                  assignedUsers={assignedUsers}
                  hideTimeWindowControls={hideTimeWindowControls}
                  workItemsById={workItemsById}
                  currentTimeOffsetPx={currentTimeOffsetPx}
                />
              );
            })}
          </SortableContext>
          
          {/* Empty state */}
          {sortedUserColumns.length === 0 && (
            <div className="flex-1 flex items-center justify-center py-12 min-w-[300px]">
              <div className="text-center text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p className="font-medium">No assignees found</p>
                <p className="text-sm">Add users or active operations teams to see time allocations</p>
              </div>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}

